import React, { useState, useEffect, useCallback, useContext } from "react";
import { useLocation } from "react-router-dom";
import { MenuItem } from "@mui/material";
import { BrandingContext } from "../BrandingProvider";
import { usePermissions } from "./login/PermissionContext";
import { generateInvoicePdf } from "./ipd-billing/utils/billingPdf";
import { C, fmtINR } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import PatientSearchBar from "./ipd-billing/components/PatientSearchBar";
import PdfPreviewModal from "./ipd-billing/components/PdfPreviewModal";
import IpdButton from "./ipd/utils/IpdButton";
import IpdFormField from "./ipd/utils/IpdFormField";
import IpdTextField from "./ipd/utils/IpdTextField";
import IpdDateField from "./ipd/utils/IpdDateField";
import IpdDataGrid from "./ipd/utils/IpdDataGrid";
import { useBillingPermissions } from "./ipd-billing/useBillingPermissions";
import { getActiveCashCounterSession } from "./ipd-billing/useCashCounter";
import {
  resolveAdmissionId,
  persistAdmissionContext,
  restoreAdmissionContext,
  hydrateRestoredPatient,
  clearAdmissionContext,
  listAllocatedAdmissions,
  admissionToPatientContext,
  listInvoices,
  getInvoiceScreen,
  getBillPreview,
  generateInvoice,
  issueInvoice,
  collectPayment,
  getPatientPan,
  listPayments,
  listDeposits,
  adjustDepositsRefunds,
  mapDepositsToWallet,
  mapInvoiceScreenToView,
  pickSplitBilling,
  buildSettlementSteps,
  listServiceGroups,
  listServiceClassifications,
  listMedicalServices,
  listDiscountReasons,
  createAdhocDiscount,
  createAdhocDiscountsBatch,
  applyAdhocDiscounts,
  listAdhocDiscounts,
  updateAdhocDiscount,
  revokeAdhocDiscount,
  resetAdhocDiscounts,
  requestInvoiceCancel,
  printDocument,
  requestPrintReset,
  toBillingError,
  BILLING_PERMS,
  PAYMENT_MODE_TO_API,
} from "./ipd-billing/billing-api";
import { ensureBillingSetup, listBillingAccountPackages } from "./ipd-billing/package-billing-api";

const PAYMENT_MODES = ["UPI", "Card", "Cash", "Bank Transfer"];

// ── SKELETON ──────────────────────────────────────────────────────────────────
const shimmerStyle = {
  background: "linear-gradient(90deg,#e9ecef 25%,#f8f9fa 50%,#e9ecef 75%)",
  backgroundSize: "200% 100%",
  animation: "ipdShimmer 1.4s infinite",
  borderRadius: "4px",
};

function EmptyState({ title, message, action }) {
  return (
    <div style={{ padding: "64px 32px", textAlign: "center" }}>
      <div style={{ fontSize: "15px", fontWeight: "700", color: C.text, marginBottom: "8px" }}>{title}</div>
      <p style={{ margin: "0 0 20px", fontSize: "13px", color: C.textSub, lineHeight: 1.6 }}>{message}</p>
      {action}
    </div>
  );
}

// ── GENERATE INVOICE MODAL ────────────────────────────────────────────────────
function GenerateInvoiceModal({ onClose, onGenerate, busy }) {
  const [form, setForm] = useState({
    // Single-draft lifecycle: INTERIM refreshes the one open DRAFT and leaves it
    // for a separate Issue. FINAL is the settlement step, so it generates and
    // issues in one go (backend `issue` flag) — same result as Issue Invoice.
    invoice_type: "INTERIM",
    due_date: new Date().toISOString().split("T")[0],
    remarks: "",
  });
  const isFinal = form.invoice_type === "FINAL";
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: "440px", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 18px", fontSize: "17px", fontWeight: "800", color: C.text }}>Generate Invoice</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <IpdFormField label="Invoice Type">
              <IpdTextField select fullWidth value={form.invoice_type} onChange={(e) => setForm((p) => ({ ...p, invoice_type: e.target.value }))}>
                <MenuItem value="INTERIM">Interim (mid-stay)</MenuItem>
                <MenuItem value="FINAL">Final (discharge)</MenuItem>
              </IpdTextField>
            </IpdFormField>
          </div>
          <div>
            <IpdFormField label="Due Date">
              <IpdDateField fullWidth value={form.due_date} onChange={(iso) => setForm((p) => ({ ...p, due_date: iso }))} />
            </IpdFormField>
          </div>
          <div>
            <IpdFormField label="Remarks">
              <IpdTextField fullWidth value={form.remarks} onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))} placeholder="Optional" />
            </IpdFormField>
          </div>
          <p style={{ margin: 0, fontSize: "12px", color: C.textSub, lineHeight: 1.5 }}>
            Generating reuses this admission's single draft and re-pulls every uninvoiced
            charge — it never creates a second invoice. Issue the draft separately to settle.
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <IpdButton variant="outlined" onClick={onClose} disabled={busy} sx={{ flex: 1 }}>Cancel</IpdButton>
          <IpdButton onClick={() => onGenerate(form)} disabled={busy} loading={busy} sx={{ flex: 2 }}>
            Generate
          </IpdButton>
        </div>
      </div>
    </div>
  );
}

// ── APPLY DEPOSIT MODAL ───────────────────────────────────────────────────────
function ApplyDepositModal({ invoice, avail, onClose, onSubmit, busy }) {
  const outstanding = Number(invoice?.outstanding_amount ?? invoice?.net_payable ?? 0);
  const defaultAmt = Math.min(outstanding, avail);
  const [amount, setAmount] = useState(defaultAmt > 0 ? defaultAmt.toFixed(2) : "");
  const [remarks, setRemarks] = useState("Settle bill from admission deposit");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { setError("Enter a valid amount."); return; }
    if (amt > avail + 0.01) { setError(`Amount exceeds available deposit ${fmtINR(avail)}.`); return; }
    if (amt > outstanding + 0.01) { setError(`Amount exceeds invoice outstanding ${fmtINR(outstanding)}.`); return; }
    onSubmit({ amount: amt.toFixed(2), remarks });
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: "440px", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: "800", color: C.text }}>Apply Deposit to Bill</h3>
        <p style={{ margin: "0 0 4px", fontSize: "12px", color: C.textSub }}>
          Invoice {invoice?.invoice_no || "—"} · Outstanding {fmtINR(outstanding)}
        </p>
        <p style={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>
          Deposit available: {fmtINR(avail)}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <IpdFormField label="Amount (₹)">
              <IpdTextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setError(""); }}
                placeholder={defaultAmt ? defaultAmt.toFixed(2) : "0"}
                sx={{ "& .MuiOutlinedInput-input": { fontSize: "18px", fontWeight: 700 } }}
              />
            </IpdFormField>
          </div>
          <div>
            <IpdFormField label="Remarks">
              <IpdTextField fullWidth value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </IpdFormField>
          </div>
          {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error}</div>}
        </div>
        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <IpdButton variant="outlined" onClick={onClose} disabled={busy} sx={{ flex: 1 }}>Cancel</IpdButton>
          <IpdButton onClick={handleSubmit} disabled={busy || avail <= 0} loading={busy} sx={{ flex: 2 }}>
            Apply to Bill
          </IpdButton>
        </div>
      </div>
    </div>
  );
}

// ── COLLECT PAYMENT MODAL ─────────────────────────────────────────────────────
// PAN is required at payment collection for bills of ₹1,00,000 and above (income-tax rule).
const PAN_THRESHOLD = 100000;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

function CollectPaymentModal({ invoice, admissionId, amountDue, onClose, onSubmit, busy }) {
  // Default to the patient's real due (co-pay + deposit aware), not the invoice-level
  // outstanding which still includes the insurer-approved portion the patient doesn't owe.
  const outstanding = Number(amountDue ?? invoice?.patient_payable_amt ?? invoice?.net_payable ?? 0);
  // The PAN gate keys off the whole bill (invoice net), not the amount being paid now.
  const billTotal = Number(invoice?.net_amount ?? invoice?.net_payable ?? 0);
  const panRequired = billTotal >= PAN_THRESHOLD;
  const [form, setForm] = useState({
    amount: outstanding.toFixed(2),
    mode: "UPI",
    reference: "",
    remarks: "",
    pan: "",
  });
  const [panOnFile, setPanOnFile] = useState(false);
  const [noPan, setNoPan] = useState(false); // cashier ticks this when the patient has no PAN
  const [error, setError] = useState("");
  const labelStyle = { fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: "5px" };

  // Prefill the PAN already stored on this admission (so the cashier just proceeds).
  useEffect(() => {
    if (!panRequired || !admissionId) return;
    let alive = true;
    getPatientPan(admissionId, invoice?.id || invoice?.invoice_id)
      .then((res) => {
        if (!alive || !res?.panNumber) return;
        setForm((p) => ({ ...p, pan: res.panNumber }));
        setPanOnFile(Boolean(res.present));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [panRequired, admissionId, invoice?.id, invoice?.invoice_id]);

  // Valid when not required, or the patient has no PAN (ticked), or a well-formed PAN is entered.
  const panValue = String(form.pan || "").trim().toUpperCase();
  const panFieldValid = PAN_RE.test(panValue);
  const panValid = !panRequired || noPan || panFieldValid;
  const showPanFormatError = panRequired && !noPan && panValue.length === 10 && !panFieldValid;
  const compactFieldSx = { "& .MuiFormHelperText-root": { display: "none" } };

  const handleSubmit = () => {
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { setError("Enter a valid amount."); return; }
    if (amt > outstanding + 0.01) { setError(`Amount cannot exceed outstanding ${fmtINR(outstanding)}.`); return; }
    if (panRequired && !noPan && !panFieldValid) {
      setError(showPanFormatError
        ? "Enter a valid PAN (ABCDE1234F)."
        : `PAN is mandatory for bills of ${fmtINR(PAN_THRESHOLD)} and above.`);
      return;
    }
    onSubmit({
      ...form,
      amount: amt.toFixed(2),
      pan: panRequired && !noPan ? String(form.pan).trim().toUpperCase() : undefined,
      panNotAvailable: panRequired && noPan,
    });
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: "440px", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: "800", color: C.text }}>Collect Payment</h3>
        <p style={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>Outstanding: {fmtINR(outstanding)}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <IpdFormField label="Amount (₹)" sx={compactFieldSx}>
              <IpdTextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={form.amount}
                onChange={(e) => { setForm((p) => ({ ...p, amount: e.target.value })); setError(""); }}
                sx={{ "& .MuiOutlinedInput-input": { fontSize: "18px", fontWeight: 700 } }}
              />
            </IpdFormField>
          </div>
          <div>
            <label style={labelStyle}>Payment Mode</label>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {PAYMENT_MODES.map((m) => (
                <button key={m} type="button" onClick={() => setForm((p) => ({ ...p, mode: m }))}
                  style={{ flex: "1 1 45%", padding: "8px", borderRadius: "8px", border: `1.5px solid ${form.mode === m ? C.primary : C.border}`, background: form.mode === m ? C.primary : "#fff", color: form.mode === m ? "#fff" : C.text, fontSize: "12px", fontWeight: "600", cursor: "pointer" }}
                >{m}</button>
              ))}
            </div>
          </div>
          <div>
            <IpdFormField label="Transaction Reference" sx={compactFieldSx}>
              <IpdTextField fullWidth value={form.reference} onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))} placeholder="UTR / receipt no." />
            </IpdFormField>
          </div>
          <div>
            <IpdFormField label="Remarks" sx={compactFieldSx}>
              <IpdTextField fullWidth value={form.remarks} onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))} placeholder="Optional" />
            </IpdFormField>
          </div>
          {panRequired && (
            <div>
              {!noPan && (
                <>
                  <IpdFormField label="PAN Number" required sx={compactFieldSx}>
                    <IpdTextField
                      fullWidth
                      value={form.pan}
                      error={showPanFormatError}
                      onChange={(e) => { setForm((p) => ({ ...p, pan: e.target.value.toUpperCase() })); setError(""); }}
                      inputProps={{ maxLength: 10, style: { textTransform: "uppercase", letterSpacing: "1px" } }}
                      placeholder="ABCDE1234F"
                    />
                  </IpdFormField>
                  <div style={{ fontSize: "11px", fontWeight: "600", marginTop: "4px", lineHeight: 1.45 }}>
                    {showPanFormatError && (
                      <div style={{ color: C.danger }}>Enter a valid PAN (ABCDE1234F).</div>
                    )}
                    {panFieldValid && panOnFile ? (
                      <div style={{ color: C.success }}>PAN on file for this patient — verify and proceed.</div>
                    ) : !panFieldValid ? (
                      <div style={{ color: C.danger }}>
                        PAN is mandatory for bills of {fmtINR(PAN_THRESHOLD)} and above. Please ask the patient, or tick below if they have none.
                      </div>
                    ) : null}
                  </div>
                </>
              )}
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: C.text, marginTop: "8px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={noPan}
                  onChange={(e) => { setNoPan(e.target.checked); setError(""); }}
                />
                Patient has no PAN
              </label>
              {noPan && (
                <div style={{ fontSize: "11px", color: C.textSub, fontWeight: "600", marginTop: "5px" }}>
                  Proceeding without PAN — recorded as “PAN not available”.
                </div>
              )}
            </div>
          )}
          {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error}</div>}
        </div>
        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <IpdButton variant="outlined" onClick={onClose} disabled={busy} sx={{ flex: 1 }}>Cancel</IpdButton>
          <IpdButton onClick={handleSubmit} disabled={busy || !panValid} loading={busy} sx={{ flex: 2 }}>
            Record Payment
          </IpdButton>
        </div>
      </div>
    </div>
  );
}

// ── REQUEST INVOICE CANCEL MODAL (Part E) ─────────────────────────────────────
function RequestInvoiceCancelModal({ invoice, activeReceipts, onClose, onSubmit, busy }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const blocked = activeReceipts.length > 0;

  const submit = () => {
    if (!reason.trim()) { setError("Reason is required."); return; }
    onSubmit(reason.trim());
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: "460px", maxWidth: "92vw", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: "800", color: C.text }}>Cancel Invoice</h3>
        <p style={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>
          Invoice {invoice?.invoice_number || ""} · {fmtINR(Number(invoice?.net_amount ?? invoice?.net_payable ?? 0))}
        </p>

        {blocked ? (
          <div style={{ background: "#fdecea", border: `1px solid #f5c2c7`, borderRadius: "10px", padding: "14px 16px" }}>
            <div style={{ fontSize: "13px", fontWeight: "700", color: C.danger, marginBottom: "6px" }}>Cancel receipt(s) first</div>
            <p style={{ margin: "0 0 8px", fontSize: "12px", color: C.text, lineHeight: 1.5 }}>
              This invoice has {activeReceipts.length} active receipt{activeReceipts.length > 1 ? "s" : ""}. Cancel {activeReceipts.length > 1 ? "them" : "it"} from the Payments/Receipts table before cancelling the invoice.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {activeReceipts.map((r) => (
                <span key={r.id || r.payment_id || r.payment_number} style={{ fontSize: "11px", fontWeight: "700", background: "#fff", border: `1px solid #f5c2c7`, borderRadius: "100px", padding: "3px 10px", color: C.danger }}>
                  {r.payment_number || r.receipt_number || r.number}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <IpdFormField label="Reason" required error={error}>
              <IpdTextField
                fullWidth
                multiline
                rows={3}
                value={reason}
                onChange={(e) => { setReason(e.target.value); setError(""); }}
                placeholder="Why is this invoice being cancelled?"
                error={Boolean(error)}
              />
            </IpdFormField>
          </div>
        )}

        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <IpdButton variant="outlined" onClick={onClose} disabled={busy} sx={{ flex: 1 }}>Close</IpdButton>
          {!blocked && (
            <IpdButton tone="red" onClick={submit} disabled={busy} loading={busy} sx={{ flex: 2 }}>
              Cancel Invoice
            </IpdButton>
          )}
        </div>
      </div>
    </div>
  );
}

// ── REQUEST ORIGINAL REPRINT MODAL (Part F) ───────────────────────────────────
function PrintResetModal({ onClose, onSubmit, busy }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    if (!reason.trim()) { setError("Reason is required."); return; }
    onSubmit(reason.trim());
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: "440px", maxWidth: "92vw", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: "800", color: C.text }}>Request Original Reprint</h3>
        <p style={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>
          Resets the print counter so the next print is tagged ORIGINAL again. Needs approval.
        </p>
        <IpdFormField label="Reason" required error={error}>
          <IpdTextField
            fullWidth
            multiline
            rows={3}
            value={reason}
            onChange={(e) => { setReason(e.target.value); setError(""); }}
            placeholder="Why is an original reprint needed?"
            error={Boolean(error)}
          />
        </IpdFormField>
        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <IpdButton variant="outlined" onClick={onClose} disabled={busy} sx={{ flex: 1 }}>Cancel</IpdButton>
          <IpdButton onClick={submit} disabled={busy} loading={busy} sx={{ flex: 2 }}>
            Submit Request
          </IpdButton>
        </div>
      </div>
    </div>
  );
}

// ── PRINT OPTIONS MODAL ───────────────────────────────────────────────────────
// Two-step chooser: letterhead first, then bill format. Both choices are handed to
// the print handler and shape the generated PDF.
// Two-step chooser: letterhead first, then bill format. Both choices are handed to
// the print handler and shape the generated PDF.
const PRINT_LETTERHEAD_OPTIONS = [
  { k: "WITH", l: "With letterhead", d: "Facility header and footer band on every page" },
  { k: "WITHOUT", l: "Without letterhead", d: "For printing on pre-printed stationery" },
];
const PRINT_FORMAT_OPTIONS = [
  { k: "SUMMARY", l: "Summary", d: "Category totals and settlement only" },
  { k: "DETAILED", l: "Detailed", d: "Every line item grouped by category" },
  { k: "COMBINED", l: "Combined", d: "Summary followed by the detailed bill" },
];
const FORMAT_LABEL = Object.fromEntries(PRINT_FORMAT_OPTIONS.map((o) => [o.k, o.l]));

function PrintOptionsModal({ onClose, onSubmit, busy }) {
  const [letterhead, setLetterhead] = useState(null);

  const optionButton = (opt, onPick) => (
    <button
      key={opt.k}
      type="button"
      onClick={() => onPick(opt.k)}
      disabled={busy}
      style={{
        display: "block", width: "100%", textAlign: "left", cursor: busy ? "default" : "pointer",
        background: "#fff", border: `1px solid ${C.border}`, borderRadius: "10px",
        padding: "12px 14px", font: "inherit",
      }}
    >
      <span style={{ display: "block", fontSize: "14px", fontWeight: "700", color: C.text }}>{opt.l}</span>
      <span style={{ display: "block", marginTop: "2px", fontSize: "12px", color: C.textSub }}>{opt.d}</span>
    </button>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: "440px", maxWidth: "92vw", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: "800", color: C.text }}>
          {letterhead ? "Select Bill Format" : "Print Bill"}
        </h3>
        <p style={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>
          {letterhead ? "Step 2 of 2 — how much detail should the bill carry?" : "Step 1 of 2 — print with or without the letterhead?"}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {letterhead
            ? PRINT_FORMAT_OPTIONS.map((opt) => optionButton(opt, (format) => onSubmit({ letterhead, format })))
            : PRINT_LETTERHEAD_OPTIONS.map((opt) => optionButton(opt, setLetterhead))}
        </div>
        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <IpdButton variant="outlined" onClick={letterhead ? () => setLetterhead(null) : onClose} disabled={busy} sx={{ flex: 1 }}>
            {letterhead ? "Back" : "Cancel"}
          </IpdButton>
        </div>
      </div>
    </div>
  );
}

// ── ADDITIONAL DISCOUNT MODAL ─────────────────────────────────────────────────
const DISCOUNT_SCOPES = [
  { k: "TOTAL", l: "Whole bill" },
  { k: "GROUP", l: "Service group" },
  { k: "CLASSIFICATION", l: "Classification" },
  { k: "SERVICE", l: "Medical service" },
  { k: "PACKAGE", l: "Package" },
];

const normalizeText = (v) => String(v ?? "").trim().toLowerCase().replace(/[\s_-]+/g, " ");

// Reusable checkbox multi-select dropdown (groups / classifications / services).
function MultiSelectDropdown({ label, placeholder, options, selectedMap, onToggle, open, setOpen, searchable, search, onSearch, disabled, disabledHint, emptyHint }) {
  const inputStyle = { width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: "7px", fontSize: "13px", color: C.text, outline: "none", boxSizing: "border-box", background: "#fff" };
  const labelStyle = { fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: "5px" };
  const selected = Object.values(selectedMap);
  return (
    <div>
      <label style={labelStyle}>{label}{selected.length > 0 ? ` (${selected.length} selected)` : ""}</label>
      <div style={{ position: "relative" }}>
        <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)}
          style={{ ...inputStyle, cursor: disabled ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", textAlign: "left", opacity: disabled ? 0.6 : 1 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: selected.length ? C.text : C.textSub }}>
            {disabled ? (disabledHint || placeholder) : selected.length ? selected.map((s) => s.name).join(", ") : placeholder}
          </span>
          <SvgIcon name="chevron-down" size={16} color={C.textSub} />
        </button>
        {open && !disabled && (
          <>
            <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 5 }} />
            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 6, background: "#fff", border: `1px solid ${C.border}`, borderRadius: "7px", boxShadow: "0 8px 24px rgba(13,35,63,0.18)", overflow: "hidden" }}>
              {searchable && (
                <div style={{ padding: "8px", borderBottom: `1px solid ${C.border}` }}>
                  <IpdTextField fullWidth autoFocus value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search…" />
                </div>
              )}
              <div style={{ maxHeight: "180px", overflowY: "auto" }}>
                {options.length === 0 ? (
                  <div style={{ padding: "12px", fontSize: "12px", color: C.textSub }}>{emptyHint || "No options."}</div>
                ) : (
                  options.map((o, idx) => {
                    const checked = Boolean(selectedMap[o.id]);
                    return (
                      <label key={o.id} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", borderTop: idx === 0 ? "none" : `1px solid ${C.border}`, cursor: "pointer", fontSize: "13px", color: C.text, background: checked ? C.accent : "transparent" }}>
                        <input type="checkbox" checked={checked} onChange={() => onToggle(o)} />
                        {o.name}{o.code ? <span style={{ color: C.textSub }}>{` (${o.code})`}</span> : null}
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const DISCOUNT_LEVEL_LABEL = { TOTAL: "Whole bill", GROUP: "Service group", CLASSIFICATION: "Classification", SERVICE: "Medical service", PACKAGE: "Package" };

// One committed adhoc-discount config — inline-editable type/value with save + remove.
function CommittedDiscountRow({ discount, busy, onSave, onRemove }) {
  const [type, setType] = useState(discount.type === "PERCENTAGE" ? "PERCENT" : "FLAT");
  const [value, setValue] = useState(String(discount.value ?? ""));
  // Re-sync if the underlying config changes (e.g. after a refresh).
  useEffect(() => {
    setType(discount.type === "PERCENTAGE" ? "PERCENT" : "FLAT");
    setValue(String(discount.value ?? ""));
  }, [discount.id, discount.type, discount.value]);

  const apiType = type === "PERCENT" ? "PERCENTAGE" : "AMOUNT";
  const dirty = apiType !== discount.type || Number(value) !== Number(discount.value);
  const valid = Number(value) > 0 && !(type === "PERCENT" && Number(value) > 100);
  const levelLabel = DISCOUNT_LEVEL_LABEL[discount.level] || discount.level;
  // Name for the discount's own level (a classification config also echoes its
  // parent group name, so pick by level rather than first-non-null).
  const levelName =
    discount.level === "SERVICE" ? discount.serviceName
    : discount.level === "CLASSIFICATION" ? discount.classificationName
    : discount.level === "GROUP" ? discount.groupName
    : discount.level === "PACKAGE" ? discount.packageName
    : null;
  const targetId = discount.serviceId ?? discount.classificationId ?? discount.groupId ?? discount.packageAssignmentId;
  const name = levelName || discount.targetName
    || (discount.level === "TOTAL" ? "Whole bill" : targetId != null ? `${levelLabel} #${targetId}` : levelLabel);

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: "8px", padding: "12px 14px", background: "#f8f9fa", display: "flex", flexDirection: "column", gap: "10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
        <span style={{ fontSize: "13px", fontWeight: "700", color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {name}<span style={{ color: C.textSub, fontWeight: "400" }}>{` · ${levelLabel}`}</span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
          <button type="button" title="Remove" disabled={busy} onClick={() => onRemove(discount.id)}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "22px", height: "22px", border: "none", borderRadius: "6px", background: "transparent", color: C.textSub, cursor: busy ? "not-allowed" : "pointer", padding: 0 }}>
            <SvgIcon name="trash" size={15} color={C.danger} />
          </button>
        </span>
      </div>
      <div style={{ display: "flex", gap: "8px" }}>
        <div style={{ display: "flex", gap: "6px" }}>
          {[{ k: "FLAT", l: "₹" }, { k: "PERCENT", l: "%" }].map((opt) => (
            <button key={opt.k} type="button" disabled={busy} onClick={() => setType(opt.k)}
              style={{ padding: "8px 14px", borderRadius: "7px", border: `1.5px solid ${type === opt.k ? C.primary : C.border}`, background: type === opt.k ? C.primary : "#fff", color: type === opt.k ? "#fff" : C.text, fontSize: "13px", fontWeight: "700", cursor: busy ? "not-allowed" : "pointer" }}
            >{opt.l}</button>
          ))}
        </div>
        <IpdTextField type="number" inputProps={{ min: 0 }} value={value} disabled={busy}
          onChange={(e) => setValue(e.target.value)}
          placeholder={type === "PERCENT" ? "Discount %" : "Discount ₹"}
          sx={{ flex: 1 }} />
        {dirty && (
          <button type="button" title="Save" disabled={busy || !valid}
            onClick={() => onSave(discount.id, { discount_type: apiType, discount_value: Number(value).toFixed(2) })}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "8px", width: "38px", borderRadius: "7px", border: "none", background: valid ? C.primary : C.border, color: valid ? "#fff" : C.textSub, cursor: valid && !busy ? "pointer" : "not-allowed", flexShrink: 0 }}
          ><SvgIcon name="check" size={16} color={valid ? "#fff" : C.textSub} /></button>
        )}
      </div>
      {discount.reason && (
        <div style={{ fontSize: "12px", color: C.textSub, borderTop: `1px dashed ${C.border}`, paddingTop: "8px" }}>
          <span style={{ fontWeight: "600" }}>Reason:</span> {discount.reason}
        </div>
      )}
    </div>
  );
}

function AdditionalDiscountModal({ invoice, admissionId, applied, onClose, onSaveDiscount, onApplyDiscount, onUpdateDiscount, onRemoveDiscount, onResetDiscounts, busy }) {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [committed, setCommitted] = useState([]); // saved adhoc-discount configs

  // Additional discount only ever targets charges that haven't been invoiced
  // yet — always pull lines + totals from bill-preview (the uninvoiced ledger),
  // never from an already-generated invoice's line_items.
  useEffect(() => {
    if (!admissionId) return;
    let alive = true;
    setPreviewLoading(true);
    getBillPreview(admissionId)
      .then((data) => { if (alive) setPreview(data); })
      .catch(() => { if (alive) setPreview(null); })
      .finally(() => { if (alive) setPreviewLoading(false); });
    return () => { alive = false; };
  }, [admissionId]);

  // Load the saved adhoc-discount configs so they can be edited / removed.
  const reloadCommitted = useCallback(() => {
    if (!admissionId) return Promise.resolve();
    return listAdhocDiscounts(admissionId)
      .then((list) => setCommitted(list))
      .catch(() => setCommitted([]));
  }, [admissionId]);
  useEffect(() => { reloadCommitted(); }, [reloadCommitted]);

  // Re-fetch both the bill preview and the saved-config list after a change.
  const reloadAll = useCallback(async () => {
    if (!admissionId) return;
    const fresh = await getBillPreview(admissionId).catch(() => null);
    if (fresh) setPreview(fresh);
    await reloadCommitted();
  }, [admissionId, reloadCommitted]);

  const lineItems = preview?.lines || [];
  const previewColumns = [
    { field: 'description', headerName: 'Description', flex: 1, renderCell: (params) => params.value },
    { field: 'amount', headerName: 'Amount', width: 140, headerAlign: 'right', align: 'right', valueFormatter: (params) => fmtINR(params.value) },
  ];
  const itemsSourceLabel = " (uninvoiced)";
  const subtotal = Number(preview?.subtotal) > 0
    ? Number(preview.subtotal)
    : lineItems.reduce((s, i) => s + Number(i.amount || 0), 0);
  // Discounts already committed (from bill-preview), or the client-side `applied` fallback.
  const appliedAmount = preview
    ? Number(preview.line_adhoc_discount_amount || 0) + Number(preview.bill_total_discount_amount || 0)
    : Number(applied?.amount || 0);

  const [scope, setScope] = useState("TOTAL");
  // TOTAL scope uses a single type/value; the line-scoped levels use per-target configs.
  const [type, setType] = useState("FLAT"); // FLAT | PERCENT (TOTAL scope)
  const [value, setValue] = useState("");   // TOTAL scope
  const [reason, setReason] = useState(""); // selected reason label (from the reasons API)
  const [reasonOptions, setReasonOptions] = useState([]); // predefined reasons (mock API for now)
  const [remarks, setRemarks] = useState(""); // free-text note alongside the chosen reason
  const [error, setError] = useState("");

  // Hierarchy data
  const [groups, setGroups] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [services, setServices] = useState([]);
  // PACKAGE scope: the admission's active package assignments (multi-package). Each is a
  // discount target whose base is its own package price (applied_package_price).
  const [packageOptions, setPackageOptions] = useState([]);
  const [pkgLoading, setPkgLoading] = useState(false);
  const [pkgError, setPkgError] = useState("");
  // CLASSIFICATION scope: services per selected classification (for line resolution),
  // keyed by classificationId. The catalog API requires group_id + classification_id.
  const [classServices, setClassServices] = useState({});
  // GROUP scope: services per selected group (union across the group's classifications),
  // keyed by groupId — used to resolve which line items each selected group covers.
  const [groupServices, setGroupServices] = useState({});
  // Single-select FILTERS used to narrow the bottom level (not the discount target):
  //   CLASSIFICATION scope → groupId filters the classification list.
  //   SERVICE scope        → groupId + classificationId filter the service search.
  const [groupId, setGroupId] = useState("");
  const [classificationId, setClassificationId] = useState("");
  // Bottom-level MULTI selections (the discount targets). Keep full objects so the
  // selection survives across async searches (search results mutate `services`).
  const [selectedGroupMap, setSelectedGroupMap] = useState({});     // GROUP scope
  const [selectedClassMap, setSelectedClassMap] = useState({});     // CLASSIFICATION scope
  const [selectedServiceMap, setSelectedServiceMap] = useState({}); // SERVICE scope
  const [selectedPackageMap, setSelectedPackageMap] = useState({}); // PACKAGE scope
  // Per-target discount config, keyed by `${scope}:${id}` → { type, value }.
  const [targetConfig, setTargetConfig] = useState({});
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);
  const [classMenuOpen, setClassMenuOpen] = useState(false);
  const [serviceMenuOpen, setServiceMenuOpen] = useState(false);
  const [packageMenuOpen, setPackageMenuOpen] = useState(false);
  const [serviceSearch, setServiceSearch] = useState("");
  const [hLoading, setHLoading] = useState(false);
  const [hError, setHError] = useState("");
  const [appliedDone, setAppliedDone] = useState(false); // post-apply confirmation (flow step 8)
  const [savedUnapplied, setSavedUnapplied] = useState(false); // a config was saved after the last apply
  // "Apply to all" — one shared type/value fanned out to every per-target config row.
  const [bulkAll, setBulkAll] = useState(false);
  const [bulkType, setBulkType] = useState("FLAT");
  const [bulkValue, setBulkValue] = useState("");

  const labelStyle = { fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: "8px" };

  // SERVICE scope uses the full hierarchy: Group → Classification → Medical service.
  // The "bottom level" (the discount target) is multi-select per scope.
  const needsGroup = scope === "GROUP" || scope === "CLASSIFICATION" || scope === "SERVICE";
  const groupIsTarget = scope === "GROUP";          // groups are the multi target
  const needsGroupFilter = scope === "CLASSIFICATION" || scope === "SERVICE"; // single group filter
  const needsClassFilter = scope === "SERVICE";     // single classification filter
  const classIsTarget = scope === "CLASSIFICATION"; // classifications are the multi target
  const serviceIsTarget = scope === "SERVICE";      // services are the multi target
  const packageIsTarget = scope === "PACKAGE";      // assigned packages are the multi target

  const resetSelections = () => {
    setGroupId(""); setClassificationId("");
    setSelectedGroupMap({}); setSelectedClassMap({}); setSelectedServiceMap({}); setSelectedPackageMap({});
    setTargetConfig({}); setClassServices({}); setGroupServices({});
    setBulkAll(false); setBulkType("FLAT"); setBulkValue("");
  };

  // PACKAGE scope: load the admission's active packages (resolve billing account first).
  // Also load them when a saved PACKAGE discount exists, so its row can show the package name.
  const hasCommittedPackage = committed.some((d) => d.level === "PACKAGE");
  useEffect(() => {
    if ((!packageIsTarget && !hasCommittedPackage) || !admissionId || packageOptions.length > 0) return;
    let alive = true;
    setPkgLoading(true); setPkgError("");
    ensureBillingSetup(admissionId)
      .then((setup) => (setup?.billingAccountId ? listBillingAccountPackages(setup.billingAccountId) : []))
      .then((list) => { if (alive) setPackageOptions((Array.isArray(list) ? list : []).filter((p) => p.status === "ACTIVE")); })
      .catch((err) => { if (alive) setPkgError(err.message || "Failed to load packages"); })
      .finally(() => { if (alive) setPkgLoading(false); });
    return () => { alive = false; };
  }, [packageIsTarget, hasCommittedPackage, admissionId, packageOptions.length]);

  // Load groups when first needed
  useEffect(() => {
    if (!needsGroup || groups.length > 0) return;
    let alive = true;
    setHLoading(true); setHError("");
    listServiceGroups(admissionId)
      .then((list) => { if (alive) setGroups(list); })
      .catch((err) => { if (alive) setHError(err.message || "Failed to load service groups"); })
      .finally(() => { if (alive) setHLoading(false); });
    return () => { alive = false; };
  }, [needsGroup, groups.length, admissionId]);

  // Load the predefined discount reasons once (mock API for now).
  useEffect(() => {
    let alive = true;
    listDiscountReasons()
      .then((list) => { if (alive) setReasonOptions(list); })
      .catch(() => { if (alive) setReasonOptions([]); });
    return () => { alive = false; };
  }, []);

  // Load classifications when the group filter changes
  useEffect(() => {
    if (!needsGroupFilter || !groupId) { setClassifications([]); return; }
    let alive = true;
    setHLoading(true); setHError("");
    listServiceClassifications(groupId, admissionId)
      .then((list) => { if (alive) setClassifications(list); })
      .catch((err) => { if (alive) setHError(err.message || "Failed to load classifications"); })
      .finally(() => { if (alive) setHLoading(false); });
    return () => { alive = false; };
  }, [needsGroupFilter, groupId, admissionId]);

  // SERVICE scope → require a Group + Classification (hierarchy), then a debounced
  // search narrowed by those filters (catalog API needs both ids).
  useEffect(() => {
    let alive = true;
    if (scope !== "SERVICE") { setServices([]); return () => { alive = false; }; }
    if (!groupId || !classificationId) { setServices([]); setHLoading(false); return () => { alive = false; }; }
    const q = serviceSearch.trim();
    const timer = setTimeout(() => {
      setHLoading(true); setHError("");
      listMedicalServices({ search: q, groupId, classificationId, admissionId })
        .then((list) => { if (alive) setServices(list); })
        .catch((err) => { if (alive) setHError(err.message || "Failed to load medical services"); })
        .finally(() => { if (alive) setHLoading(false); });
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
  }, [scope, groupId, classificationId, serviceSearch, admissionId]);

  // CLASSIFICATION scope → fetch the service list for each selected classification
  // (group_id + classification_id are both required) so we can resolve which line
  // items each one covers. Cached by classificationId.
  useEffect(() => {
    if (scope !== "CLASSIFICATION" || !groupId) return;
    const missing = Object.values(selectedClassMap).filter((c) => !classServices[c.id]);
    if (missing.length === 0) return;
    let alive = true;
    Promise.all(missing.map((c) =>
      listMedicalServices({ groupId, classificationId: c.id, admissionId })
        .then((list) => [c.id, list])
        .catch(() => [c.id, []])
    )).then((pairs) => { if (alive) setClassServices((prev) => ({ ...prev, ...Object.fromEntries(pairs) })); });
    return () => { alive = false; };
  }, [scope, groupId, selectedClassMap, classServices, admissionId]);

  // GROUP scope → resolve each selected group's services (the catalog needs a
  // classification_id, so fetch the group's classifications then union their
  // services). Used to highlight/aggregate the group's line items, since
  // bill-preview lines carry no department. Cached by groupId.
  useEffect(() => {
    if (scope !== "GROUP") return;
    const missing = Object.values(selectedGroupMap).filter((g) => !groupServices[g.id]);
    if (missing.length === 0) return;
    let alive = true;
    Promise.all(missing.map(async (g) => {
      try {
        const classes = await listServiceClassifications(g.id, admissionId);
        const perClass = await Promise.all(
          classes.map((c) => listMedicalServices({ groupId: g.id, classificationId: c.id, admissionId }).catch(() => []))
        );
        return [g.id, perClass.flat()];
      } catch {
        return [g.id, []];
      }
    })).then((pairs) => { if (alive) setGroupServices((prev) => ({ ...prev, ...Object.fromEntries(pairs) })); });
    return () => { alive = false; };
  }, [scope, selectedGroupMap, groupServices, admissionId]);

  // Resolve service lists for any *saved* GROUP/CLASSIFICATION discounts too —
  // independent of whatever the picker above currently has selected — so the
  // "pending Apply" total below can estimate their amount even right after the
  // modal opens (see amountForCommitted below).
  useEffect(() => {
    const missingClass = committed.filter((d) => d.level === "CLASSIFICATION" && d.classificationId != null && !classServices[d.classificationId]);
    const missingGroup = committed.filter((d) => d.level === "GROUP" && d.groupId != null && !groupServices[d.groupId]);
    if (missingClass.length === 0 && missingGroup.length === 0) return;
    let alive = true;
    Promise.all([
      ...missingClass.map((d) =>
        listMedicalServices({ groupId: d.groupId, classificationId: d.classificationId, admissionId })
          .then((list) => ["class", d.classificationId, list])
          .catch(() => ["class", d.classificationId, []])
      ),
      ...missingGroup.map(async (d) => {
        try {
          const classes = await listServiceClassifications(d.groupId, admissionId);
          const perClass = await Promise.all(
            classes.map((c) => listMedicalServices({ groupId: d.groupId, classificationId: c.id, admissionId }).catch(() => []))
          );
          return ["group", d.groupId, perClass.flat()];
        } catch {
          return ["group", d.groupId, []];
        }
      }),
    ]).then((results) => {
      if (!alive) return;
      const classPairs = results.filter(([kind]) => kind === "class").map(([, id, list]) => [id, list]);
      const groupPairs = results.filter(([kind]) => kind === "group").map(([, id, list]) => [id, list]);
      if (classPairs.length) setClassServices((prev) => ({ ...prev, ...Object.fromEntries(classPairs) }));
      if (groupPairs.length) setGroupServices((prev) => ({ ...prev, ...Object.fromEntries(groupPairs) }));
    });
    return () => { alive = false; };
  }, [committed, classServices, groupServices, admissionId]);

  // The selected bottom-level targets for the current scope.
  const selectedServices = Object.values(selectedServiceMap);
  const bottomTargets =
    groupIsTarget ? Object.values(selectedGroupMap)
    : classIsTarget ? Object.values(selectedClassMap)
    : serviceIsTarget ? selectedServices
    : packageIsTarget ? Object.values(selectedPackageMap)
    : [];

  // Targets picked by ticking bill line items (rather than the Group→Class→Service
  // dropdowns). When every service target came from the bill, the dropdowns are just
  // clutter, so we hide them.
  const lineDriven = serviceIsTarget && selectedServices.length > 0 && selectedServices.every((t) => t.__line);

  const toggleInMap = (setMap) => (o) => setMap((prev) => {
    const next = { ...prev };
    if (next[o.id]) delete next[o.id]; else next[o.id] = o;
    return next;
  });
  const toggleGroup = toggleInMap(setSelectedGroupMap);
  const toggleClass = toggleInMap(setSelectedClassMap);
  const toggleService = toggleInMap(setSelectedServiceMap);
  const togglePackage = toggleInMap(setSelectedPackageMap);

  // Checking a bill line item is a shortcut for a per-service discount: it drops
  // the line into the SERVICE targets (switching scope if needed) so it renders as
  // a discount-config row up top — the same view as picking a Medical service.
  const toggleLineItem = (item) => {
    if (scope !== "SERVICE") setScope("SERVICE");
    setSelectedServiceMap((prev) => {
      const next = { ...prev };
      if (next[item.id]) delete next[item.id];
      else next[item.id] = {
        id: item.id,
        name: item.description,
        code: item.service_code,
        serviceId: item.medical_service_id ?? null,
        amount: Number(item.amount || 0),
        __line: true,
      };
      return next;
    });
    setError("");
  };

  // Per-target discount config helpers.
  const cfgKey = (t) => (t.__line ? `LINE:${t.id}` : `${scope}:${t.id}`);
  const getCfg = (t) => targetConfig[cfgKey(t)] || { type: "FLAT", value: "" };
  const setCfg = (t, patch) => setTargetConfig((prev) => ({ ...prev, [cfgKey(t)]: { ...(prev[cfgKey(t)] || { type: "FLAT", value: "" }), ...patch } }));

  // Fan a single {type, value} out to every staged target (used by "Apply to all").
  const applyBulkToAll = (patch) => setTargetConfig((prev) => {
    const next = { ...prev };
    bottomTargets.forEach((t) => { next[cfgKey(t)] = { ...(next[cfgKey(t)] || { type: "FLAT", value: "" }), ...patch }; });
    return next;
  });

  // Drop a single staged target (deselect it + forget its config).
  const removeTarget = (t) => {
    if (groupIsTarget) toggleGroup(t);
    else if (classIsTarget) toggleClass(t);
    else if (serviceIsTarget) toggleService(t);
    else if (packageIsTarget) togglePackage(t);
    setTargetConfig((prev) => { const next = { ...prev }; delete next[cfgKey(t)]; return next; });
  };

  // "Apply to all" only matters with more than one target. While on, the shared
  // type/value is fanned out to every target (including any added later).
  const bulkActive = bulkAll && bottomTargets.length > 1;
  useEffect(() => {
    if (bulkActive) applyBulkToAll({ type: bulkType, value: bulkValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkActive, bottomTargets.length, bulkType, bulkValue]);

  // Clear the staged (not-yet-applied) discount inputs, keeping the scope.
  const clearStaged = () => {
    setSelectedGroupMap({}); setSelectedClassMap({}); setSelectedServiceMap({}); setSelectedPackageMap({});
    setTargetConfig({}); setValue(""); setType("FLAT"); setReason(""); setRemarks(""); setError("");
    setBulkAll(false); setBulkType("FLAT"); setBulkValue("");
  };
  const hasStagedDiscount = scope === "TOTAL" ? Number(value) > 0 : bottomTargets.length > 0;

  // Whether the ledger already reflects adhoc discounts (i.e. Apply has been run).
  // Drives `reapply_ledger` so a saved-but-unapplied config isn't silently applied
  // when edited/removed.
  const ledgerApplied = appliedAmount > 0;

  // Edit / remove a single saved discount, then refresh.
  const handleRowUpdate = async (id, patch) => {
    try { await onUpdateDiscount(id, patch, ledgerApplied); await reloadAll(); } catch { /* page surfaced error */ }
  };
  const handleRowRemove = async (id) => {
    try { await onRemoveDiscount(id, ledgerApplied); await reloadAll(); } catch { /* page surfaced error */ }
  };

  // Reset clears both the staged inputs and every committed discount.
  const handleReset = async () => {
    clearStaged();
    setSavedUnapplied(false);
    if (committed.length > 0) {
      try { await onResetDiscounts(); await reloadAll(); setAppliedDone(false); } catch { /* page surfaced error */ }
    }
  };
  const canReset = hasStagedDiscount || committed.length > 0;
  // Apply is available only when something is not-yet-committed to the ledger:
  //   • staged input,
  //   • a config saved since the last apply, or
  //   • saved configs that have never been applied (ledger still shows 0).
  // After a successful Apply it disables until the discount line items change again.
  const canApply = hasStagedDiscount || savedUnapplied || (committed.length > 0 && appliedAmount === 0);

  // Match line items against a set of medical services (by code or name).
  const matchLinesByServices = (svcList) => {
    const codes = new Set(svcList.map((s) => normalizeText(s.code)).filter(Boolean));
    const names = new Set(svcList.map((s) => normalizeText(s.name)));
    return new Set(lineItems.filter((i) =>
      codes.has(normalizeText(i.service_code)) || names.has(normalizeText(i.description))
    ).map((i) => i.id));
  };

  // Line items a single bottom-level target covers.
  const linesForTarget = (t) => {
    if (t.__line) return new Set([t.id]); // checkbox target → exactly its own line
    if (groupIsTarget) {
      // Primary match: each charged line carries its group_code (bed-agnostic). Fall back to
      // dept name / the group's resolved services for older previews without the code.
      // Primary match: each charged line carries its group_code (bed-agnostic). Fall back to
      // dept name / the group's resolved services for older previews without the code.
      const ids = new Set(lineItems.filter((i) =>
        (i.group_code && t.code && normalizeText(i.group_code) === normalizeText(t.code)) ||
        normalizeText(i.dept) === normalizeText(t.name)
      ).map((i) => i.id));
      matchLinesByServices(groupServices[t.id] || []).forEach((id) => ids.add(id));
      return ids;
    }
    if (classIsTarget) {
      // Primary match: line.classification_code (works across bed variants). Fall back to the
      // classification's resolved services (code/name) for previews without the code.
      const ids = new Set(lineItems.filter((i) =>
        i.classification_code && t.code && normalizeText(i.classification_code) === normalizeText(t.code)
      ).map((i) => i.id));
      matchLinesByServices(classServices[t.id] || []).forEach((id) => ids.add(id));
      return ids;
    }
    if (serviceIsTarget) {
      // Match the exact service_code, else the shared unique_code (bed variants), else name.
      const direct = new Set(lineItems.filter((i) =>
        (i.unique_code && t.uniqueCode && normalizeText(i.unique_code) === normalizeText(t.uniqueCode)) ||
        (i.unique_code && t.code && normalizeText(i.unique_code) === normalizeText(t.code))
      ).map((i) => i.id));
      matchLinesByServices([t]).forEach((id) => direct.add(id));
      return direct;
    }
    return new Set();
  };
  // A PACKAGE discount reduces the package's own price, not any bill line — its base is the
  // assigned package's applied price. All other scopes resolve against bill line items.
  const baseForTarget = (t) => packageIsTarget
    ? Number(t.price || 0)
    : lineItems.filter((i) => linesForTarget(t).has(i.id)).reduce((s, i) => s + Number(i.amount || 0), 0);
  // Discount amount for one target from its config (flat capped at its base when known).
  const amountForTarget = (t) => {
    const cfg = getCfg(t);
    const v = Number(cfg.value) || 0;
    const base = baseForTarget(t);
    if (cfg.type === "PERCENT") return (base * v) / 100;
    return base > 0 ? Math.min(v, base) : v;
  };

  // Union of all affected lines (for table highlighting).
  const affectedLineIds = (() => {
    if (scope === "TOTAL") return new Set(lineItems.map((i) => i.id));
    if (bottomTargets.length === 0) return null;
    const s = new Set();
    bottomTargets.forEach((t) => linesForTarget(t).forEach((id) => s.add(id)));
    return s;
  })();
  // Rows to tint in the line-items table. TOTAL scope covers every line, so
  // tinting all of them says nothing — only highlight the targeted levels.
  const highlightedLineIds = scope === "TOTAL" ? null : affectedLineIds;

  const existingDiscount = Number(invoice?.discount || 0);
  // Baseline payable for the uninvoiced charges — net_payable from bill-preview
  // already reflects any adhoc discounts already committed to the ledger.
  const totalPayable = Number(preview?.net_payable ?? subtotal);

  // Line ids a *saved* discount config covers, keyed off its level (TOTAL |
  // GROUP | CLASSIFICATION | SERVICE) — same idea as linesForTarget()/
  // baseForTarget() above, but for `committed` rows (id/name shape from
  // mapAdhocDiscount) rather than the picker's in-progress target objects.
  const committedLineIds = (d) => {
    if (d.level === "TOTAL") return new Set(lineItems.map((i) => i.id));
    if (d.level === "SERVICE") {
      return new Set(lineItems.filter((i) =>
        (d.serviceId != null && String(i.medical_service_id) === String(d.serviceId)) ||
        (d.serviceName && normalizeText(i.description) === normalizeText(d.serviceName))
      ).map((i) => i.id));
    }
    if (d.level === "GROUP") return matchLinesByServices(groupServices[d.groupId] || []);
    if (d.level === "CLASSIFICATION") return matchLinesByServices(classServices[d.classificationId] || []);
    return new Set();
  };
  const baseForCommitted = (d) => {
    if (d.level === "PACKAGE") {
      const pkg = packageOptions.find((p) => String(p.id) === String(d.packageAssignmentId));
      return Number(pkg?.appliedPackagePrice || 0);
    }
    return lineItems.filter((i) => committedLineIds(d).has(i.id)).reduce((s, i) => s + Number(i.amount || 0), 0);
  };
  // The API only returns discount_amount once Apply has evaluated a config
  // against the ledger — a saved-but-unapplied PERCENTAGE discount comes back
  // with discount_amount=null, so estimate it client-side the same way the
  // staged (not-yet-saved) preview does above, instead of trusting d.amount.
  // `baseOverride` lets a whole-bill (TOTAL) config be evaluated against the
  // post-line-discount remainder rather than its own (full-subtotal) base.
  const amountForCommitted = (d, baseOverride) => {
    const base = baseOverride != null ? baseOverride : baseForCommitted(d);
    const v = Number(d.value) || 0;
    if (String(d.type).toUpperCase() === "PERCENTAGE") return (base * v) / 100;
    return base > 0 ? Math.min(v, base) : v;
  };

  // Discounts that have been Saved but not yet committed via Apply — the
  // backend's bill total only moves once Apply runs, so estimate the pending
  // amount client-side as (every saved config's amount) minus (whatever the
  // ledger already reflects). Covers the mixed case where some discounts were
  // already applied and a new one was just saved on top.
  //
  // Whole-bill (TOTAL) discounts must be evaluated AFTER the line-level ones
  // (GROUP/CLASSIFICATION/SERVICE), mirroring the backend which posts
  // line_adhoc_discount before bill_total_discount: a whole-bill % applies to
  // the subtotal net of the other discount types, not the raw subtotal.
  const lineLevelTotal = committed
    .filter((d) => d.level !== "TOTAL")
    .reduce((s, d) => s + amountForCommitted(d), 0);
  const wholeBillBase = Math.max(0, subtotal - lineLevelTotal);
  const wholeBillTotal = committed
    .filter((d) => d.level === "TOTAL")
    .reduce((s, d) => s + amountForCommitted(d, wholeBillBase), 0);
  const committedTotal = lineLevelTotal + wholeBillTotal;
  const pendingSavedAmount = Math.max(0, committedTotal - appliedAmount);
  const estimatedPayable = Math.max(0, totalPayable - pendingSavedAmount);

  // Total additional discount being entered now (for the saved config payload only —
  // not shown in the preview until it's committed).
  const numericValue = Number(value);
  const totalDiscountAmount = scope === "TOTAL"
    ? (type === "PERCENT" ? (subtotal * (numericValue || 0)) / 100 : numericValue || 0)
    : bottomTargets.reduce((s, t) => s + amountForTarget(t), 0);

  const combinedTargetName = scope === "TOTAL" ? "Whole bill" : bottomTargets.map((t) => t.name).join(", ");

  // Validate the staged inputs and build the config payload, or set an error → null.
  const buildStagedForm = () => {
    if (!reason.trim()) { setError("A reason is required for an additional discount."); return null; }
    if (scope === "TOTAL") {
      if (!numericValue || numericValue <= 0) { setError("Enter a valid discount value."); return null; }
      if (type === "PERCENT" && numericValue > 100) { setError("Percentage cannot exceed 100%."); return null; }
    } else {
      // The group/classification single-selects only exist to reach the bottom level;
      // once targets are chosen (via the dropdown or by checking bill line items) they
      // aren't required.
      if (bottomTargets.length === 0) {
        if (needsGroupFilter && !groupId) { setError("Select a service group."); return null; }
        if (needsClassFilter && !classificationId) { setError("Select a classification."); return null; }
        setError(groupIsTarget ? "Select at least one service group." : classIsTarget ? "Select at least one classification." : "Select at least one medical service.");
        return null;
      }
      for (const t of bottomTargets) {
        const cfg = getCfg(t);
        const v = Number(cfg.value) || 0;
        if (v <= 0) { setError(`Enter a discount value for "${t.name}".`); return null; }
        if (cfg.type === "PERCENT" && v > 100) { setError(`Percentage cannot exceed 100% for "${t.name}".`); return null; }
        const base = baseForTarget(t);
        if (cfg.type === "FLAT" && base > 0 && v > base + 0.01) { setError(`Discount for "${t.name}" cannot exceed ${fmtINR(base)}.`); return null; }
      }
    }
    // Per-target lines (TOTAL = a single synthetic line).
    const lines = scope === "TOTAL"
      ? [{ discount_type: type === "PERCENT" ? "PERCENTAGE" : "AMOUNT", discount_value: numericValue }]
      : bottomTargets.map((t) => {
          const cfg = getCfg(t);
          return {
            id: t.serviceId ?? t.id, // checkbox targets carry the resolved service id
            name: t.name,
            discount_type: cfg.type === "PERCENT" ? "PERCENTAGE" : "AMOUNT",
            discount_value: Number(cfg.value),
            discount_amount: Number(amountForTarget(t).toFixed(2)),
          };
        });
    return {
      discount_level: scope,           // TOTAL | GROUP | CLASSIFICATION | SERVICE
      lines,                           // one config per selected target
      reason: reason.trim(),
      remarks: remarks.trim(),
      target_name: combinedTargetName,
      discount_amount: Number(totalDiscountAmount.toFixed(2)),
    };
  };

  // Save phase — persist the staged config(s); bill totals stay unchanged until Apply.
  const handleSave = async () => {
    const form = buildStagedForm();
    if (!form) return;
    try {
      await onSaveDiscount(form);
      await reloadAll();  // saved config now appears in the list (still un-applied)
      clearStaged();
      setAppliedDone(false);
      setSavedUnapplied(true); // there is now something new to apply
    } catch { /* page surfaced the error */ }
  };

  // Apply phase — commit saved configs to the ledger. Any staged input is saved first.
  const handleApply = async () => {
    let form = null;
    if (hasStagedDiscount) { form = buildStagedForm(); if (!form) return; }
    try {
      await onApplyDiscount(form);
      await reloadAll();
      clearStaged();
      setAppliedDone(true);
      setSavedUnapplied(false); // everything is now committed → disable Apply until a change
    } catch { /* page surfaced the error */ }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "24px" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: "760px", maxWidth: "100%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: "800", color: C.text }}>Additional Discount</h3>
        <p style={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>
          {invoice?.invoice_no || "Invoice"} · Subtotal {fmtINR(subtotal)}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* ── SCOPE ── */}
          <div>
            <label style={labelStyle}>Apply discount to</label>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {DISCOUNT_SCOPES.map((opt) => (
                <button key={opt.k} type="button"
                  onClick={() => { setScope(opt.k); resetSelections(); setError(""); setHError(""); }}
                  style={{ flex: "1 1 22%", padding: "8px", borderRadius: "8px", border: `1.5px solid ${scope === opt.k ? C.primary : C.border}`, background: scope === opt.k ? C.primary : "#fff", color: scope === opt.k ? "#fff" : C.text, fontSize: "12px", fontWeight: "600", cursor: "pointer" }}
                >{opt.l}</button>
              ))}
            </div>
          </div>

          {/* ── HIERARCHY: filters + bottom-level multi-select ──
              Hidden when the targets were picked by ticking bill line items. */}
          {needsGroupFilter && !lineDriven && (
            <div>
              <label style={labelStyle}>Service Group</label>
              <IpdTextField select fullWidth value={groupId} onChange={(e) => { setGroupId(e.target.value); setClassificationId(""); setSelectedClassMap({}); setSelectedServiceMap({}); setError(""); }}>
                <MenuItem value="">{hLoading && groups.length === 0 ? "Loading…" : "Select group"}</MenuItem>
                {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              </IpdTextField>
            </div>
          )}
          {needsClassFilter && !lineDriven && (
            <div>
              <label style={labelStyle}>Classification</label>
              <IpdTextField select fullWidth value={classificationId} disabled={!groupId} onChange={(e) => { setClassificationId(e.target.value); setSelectedServiceMap({}); setError(""); }}>
                <MenuItem value="">{!groupId ? "Select group first" : "Select classification"}</MenuItem>
                {classifications.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </IpdTextField>
            </div>
          )}
          {groupIsTarget && (
            <MultiSelectDropdown
              label="Service Group" placeholder="Select groups"
              options={groups} selectedMap={selectedGroupMap}
              onToggle={(o) => { toggleGroup(o); setError(""); }}
              open={groupMenuOpen} setOpen={setGroupMenuOpen}
              emptyHint={hLoading ? "Loading…" : "No service groups."}
            />
          )}
          {classIsTarget && (
            <MultiSelectDropdown
              label="Classification" placeholder="Select classifications"
              options={classifications} selectedMap={selectedClassMap}
              onToggle={(o) => { toggleClass(o); setError(""); }}
              open={classMenuOpen} setOpen={setClassMenuOpen}
              disabled={!groupId} disabledHint="Select group first"
              emptyHint="No classifications."
            />
          )}
          {serviceIsTarget && !lineDriven && (
            <MultiSelectDropdown
              label="Medical Service" placeholder="Select services"
              options={services} selectedMap={selectedServiceMap}
              onToggle={(o) => { toggleService(o); setError(""); }}
              open={serviceMenuOpen} setOpen={setServiceMenuOpen}
              searchable search={serviceSearch} onSearch={setServiceSearch}
              emptyHint={serviceSearch.trim() ? `No matches for “${serviceSearch}”.` : "Type to search services."}
            />
          )}
          {packageIsTarget && (
            <MultiSelectDropdown
              label="Package" placeholder="Select packages"
              options={packageOptions.map((p) => ({
                id: p.id,
                name: `${p.packageName || p.packageCode} · ${fmtINR(p.appliedPackagePrice)}`,
                code: p.packageCode,
                price: p.appliedPackagePrice,
              }))}
              selectedMap={selectedPackageMap}
              onToggle={(o) => { togglePackage(o); setError(""); }}
              open={packageMenuOpen} setOpen={setPackageMenuOpen}
              emptyHint={pkgLoading ? "Loading…" : "No active packages assigned."}
            />
          )}
          {pkgError && <div style={{ fontSize: "12px", color: C.warning, fontWeight: "600" }}>{pkgError}</div>}
          {hError && <div style={{ fontSize: "12px", color: C.warning, fontWeight: "600" }}>{hError}</div>}

          {/* ── DISCOUNT TYPE + VALUE ── */}
          {scope === "TOTAL" ? (
            <>
              <div>
                <label style={labelStyle}>Discount Type</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  {[{ k: "FLAT", l: "Flat (₹)" }, { k: "PERCENT", l: "Percent (%)" }].map((opt) => (
                    <button key={opt.k} type="button" onClick={() => { setType(opt.k); setError(""); }}
                      style={{ flex: 1, padding: "8px", borderRadius: "8px", border: `1.5px solid ${type === opt.k ? C.primary : C.border}`, background: type === opt.k ? C.primary : "#fff", color: type === opt.k ? "#fff" : C.text, fontSize: "12px", fontWeight: "600", cursor: "pointer" }}
                    >{opt.l}</button>
                  ))}
                </div>
              </div>
              <div>
                <IpdFormField label={type === "PERCENT" ? "Discount (%)" : "Discount Amount (₹)"}>
                  <IpdTextField fullWidth type="number" inputProps={{ min: 0 }} value={value}
                    onChange={(e) => { setValue(e.target.value); setError(""); }}
                    placeholder="0" />
                </IpdFormField>
                {numericValue > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px", fontSize: "12px", marginTop: "6px" }}>
                    <span style={{ color: C.textSub }}>
                      {type === "PERCENT" ? `${value}% of ${fmtINR(subtotal)}` : "Discount"}
                    </span>
                    <span style={{ color: C.textSub, whiteSpace: "nowrap" }}>
                      <span style={{ color: C.danger, fontWeight: "700" }}>– {fmtINR(totalDiscountAmount)}</span>
                      <span>{`  ·  Net ${fmtINR(Math.max(0, subtotal - totalDiscountAmount))}`}</span>
                    </span>
                  </div>
                )}
              </div>
            </>
          ) : bottomTargets.length > 0 ? (
            <div>
              <label style={labelStyle}>Discount per {groupIsTarget ? "group" : classIsTarget ? "classification" : packageIsTarget ? "package" : "service"}</label>
              {bottomTargets.length > 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", border: `1px solid ${C.border}`, borderRadius: "8px", padding: "10px 12px", marginBottom: "8px", background: "#f8f9fa" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "700", color: C.text }}>
                    <input type="checkbox" checked={bulkAll} onChange={(e) => { setBulkAll(e.target.checked); setError(""); }}
                      style={{ width: "15px", height: "15px", cursor: "pointer", accentColor: C.primary }} />
                    Apply to all
                  </label>
                  {bulkActive && (
                    <div style={{ display: "flex", gap: "8px", flex: "1 1 220px", minWidth: "200px" }}>
                      <div style={{ display: "flex", gap: "6px" }}>
                        {[{ k: "FLAT", l: "₹" }, { k: "PERCENT", l: "%" }].map((opt) => (
                          <button key={opt.k} type="button" onClick={() => { setBulkType(opt.k); setError(""); }}
                            style={{ padding: "8px 14px", borderRadius: "7px", border: `1.5px solid ${bulkType === opt.k ? C.primary : C.border}`, background: bulkType === opt.k ? C.primary : "#fff", color: bulkType === opt.k ? "#fff" : C.text, fontSize: "13px", fontWeight: "700", cursor: "pointer" }}
                          >{opt.l}</button>
                        ))}
                      </div>
                      <IpdTextField type="number" inputProps={{ min: 0 }} value={bulkValue}
                        onChange={(e) => { setBulkValue(e.target.value); setError(""); }}
                        placeholder={bulkType === "PERCENT" ? "Discount % for all" : "Discount ₹ for all"}
                        sx={{ flex: 1 }} />
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {bottomTargets.map((t) => {
                  const cfg = getCfg(t);
                  const base = baseForTarget(t);
                  const previewAmt = amountForTarget(t);      // client-side computed discount
                  const showPreview = Number(cfg.value) > 0;
                  return (
                    <div key={t.id} style={{ border: `1px solid ${C.border}`, borderRadius: "8px", padding: "10px 12px", display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
                        <span style={{ fontSize: "13px", fontWeight: "700", color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {t.name}{t.code ? <span style={{ color: C.textSub, fontWeight: "400" }}>{` (${t.code})`}</span> : null}
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                          {base > 0 && <span style={{ fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{fmtINR(base)}</span>}
                          <button type="button" title="Remove" onClick={() => { removeTarget(t); setError(""); }}
                            style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "22px", height: "22px", border: "none", borderRadius: "6px", background: "transparent", color: C.textSub, cursor: "pointer", padding: 0 }}>
                            <SvgIcon name="x" size={15} color={C.textSub} />
                          </button>
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "8px", opacity: bulkActive ? 0.55 : 1 }}>
                        <div style={{ display: "flex", gap: "6px" }}>
                          {[{ k: "FLAT", l: "₹" }, { k: "PERCENT", l: "%" }].map((opt) => (
                            <button key={opt.k} type="button" disabled={bulkActive} onClick={() => { setCfg(t, { type: opt.k }); setError(""); }}
                              style={{ padding: "8px 14px", borderRadius: "7px", border: `1.5px solid ${cfg.type === opt.k ? C.primary : C.border}`, background: cfg.type === opt.k ? C.primary : "#fff", color: cfg.type === opt.k ? "#fff" : C.text, fontSize: "13px", fontWeight: "700", cursor: bulkActive ? "not-allowed" : "pointer" }}
                            >{opt.l}</button>
                          ))}
                        </div>
                        <IpdTextField type="number" inputProps={{ min: 0 }} value={cfg.value} disabled={bulkActive}
                          onChange={(e) => { setCfg(t, { value: e.target.value }); setError(""); }}
                          placeholder={cfg.type === "PERCENT" ? "Discount %" : "Discount ₹"}
                          sx={{ flex: 1 }} />
                      </div>
                      {showPreview && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px", fontSize: "12px" }}>
                          <span style={{ color: C.textSub }}>
                            {cfg.type === "PERCENT" ? `${cfg.value}% of ${fmtINR(base)}` : "Discount"}
                          </span>
                          <span style={{ color: C.textSub, whiteSpace: "nowrap" }}>
                            <span style={{ color: C.danger, fontWeight: "700" }}>– {fmtINR(previewAmt)}</span>
                            {base > 0 && <span>{`  ·  Net ${fmtINR(Math.max(0, base - previewAmt))}`}</span>}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div>
            <label style={labelStyle}>Reason</label>
            <IpdTextField select fullWidth value={reason} onChange={(e) => { setReason(e.target.value); setError(""); }}>
              <MenuItem value="">{reasonOptions.length === 0 ? "Loading reasons…" : "Select a reason"}</MenuItem>
              {reasonOptions.map((r) => <MenuItem key={r.id} value={r.label}>{r.label}</MenuItem>)}
            </IpdTextField>
          </div>

          <div>
            <label style={labelStyle}>Remarks</label>
            <IpdTextField fullWidth multiline rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
              placeholder="Optional note — additional context for this discount" />
          </div>

          {/* ── LINE ITEMS ── */}
          <div>
            <label style={labelStyle}>Bill line items{itemsSourceLabel}</label>
            <p style={{ margin: "-4px 0 8px", fontSize: "11px", color: C.textSub }}>Click a line to add it as a per-service discount above.</p>
            <div style={{ border: `1px solid ${C.border}`, borderRadius: "8px", overflow: "hidden" }}>
              <IpdDataGrid
                rows={lineItems}
                columns={previewColumns}
                loading={previewLoading}
                onRowClick={(params) => toggleLineItem(params.row)}
                getRowId={(r) => r.id}
                getRowClassName={(params) => (highlightedLineIds?.has(params.id) ? "discount-affected-row" : "")}
                sx={{
                  border: "none",
                  flex: 1,
                  minHeight: 0,
                  "& .discount-affected-row": {
                    backgroundColor: C.accent,
                    boxShadow: `inset 3px 0 0 ${C.primary}`,
                    "&:hover": { backgroundColor: C.accent },
                  },
                }}
                pageSize={100}
                hideFooter={lineItems.length <= 100}
                autoHeight
              />
            </div>
          </div>

          {/* ── SAVED DISCOUNTS (edit / remove) ── */}
          {committed.length > 0 && (
            <div>
              <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: "8px" }}>
                <span>Saved discounts ({committed.length})</span>
                <span style={{ fontSize: "10px", fontWeight: "700", padding: "2px 7px", borderRadius: "100px", textTransform: "none", letterSpacing: 0, color: ledgerApplied ? C.success : C.textSub, background: ledgerApplied ? "rgba(25,135,84,0.12)" : "#eef1f4" }}>
                  {ledgerApplied ? "Applied to bill" : "Not applied yet"}
                </span>
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {committed.map((d) => {
                  // Billing doesn't echo the package name on saved configs — resolve it from
                  // the loaded package list so the row reads "Chest Package" not "Package #…".
                  const row = d.level === "PACKAGE" && !d.packageName
                    ? { ...d, packageName: packageOptions.find((p) => String(p.id) === String(d.packageAssignmentId))?.packageName || null }
                    : d;
                  return (
                    <CommittedDiscountRow
                      key={d.id}
                      discount={row}
                      busy={busy}
                      onSave={handleRowUpdate}
                      onRemove={handleRowRemove}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* ── TOTALS PREVIEW ── */}
          {(() => {
            const rowStyle = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", padding: "4px 0" };
            const labelStyle2 = { fontSize: "13px", color: C.textSub, flex: 1, minWidth: 0 };
            const amtStyle = { fontSize: "13px", whiteSpace: "nowrap", flexShrink: 0 };
            return (
              <div style={{ background: "#f8f9fa", borderRadius: "8px", padding: "12px 14px" }}>
                <div style={rowStyle}>
                  <span style={labelStyle2}>Subtotal</span>
                  <span style={{ ...amtStyle, color: C.text }}>{fmtINR(subtotal)}</span>
                </div>
                {existingDiscount > 0 && (
                  <div style={rowStyle}>
                    <span style={labelStyle2}>Discount ({invoice?.discount_label || "Discount"})</span>
                    <span style={{ ...amtStyle, color: C.danger, fontWeight: "600" }}>– {fmtINR(existingDiscount)}</span>
                  </div>
                )}
                {appliedAmount > 0 && (
                  <div style={rowStyle}>
                    <span style={labelStyle2}>Additional Discount{applied?.label ? ` (${applied.label})` : ""} · applied</span>
                    <span style={{ ...amtStyle, color: C.danger, fontWeight: "600" }}>– {fmtINR(appliedAmount)}</span>
                  </div>
                )}
                {/* Staged (typed but not yet Saved) discounts are intentionally NOT
                    shown here. Saved-but-not-yet-Applied configs ARE shown below as
                    an estimate, since the backend total doesn't move until Apply. */}
                {pendingSavedAmount > 0 && (
                  <div style={rowStyle}>
                    <span style={labelStyle2}>Additional Discount (saved) · pending Apply</span>
                    <span style={{ ...amtStyle, color: C.danger, fontWeight: "600" }}>– {fmtINR(pendingSavedAmount)}</span>
                  </div>
                )}
                <div style={{ borderTop: `1.5px solid ${C.border}`, margin: "8px 0" }} />
                <div style={{ ...rowStyle, alignItems: "center" }}>
                  <span style={{ fontSize: "14px", fontWeight: "800", color: C.text }}>
                    {pendingSavedAmount > 0 ? "Estimated Payable" : "Total Payable"}
                  </span>
                  <span style={{ fontSize: "16px", fontWeight: "800", color: C.primary, whiteSpace: "nowrap", flexShrink: 0 }}>
                    {estimatedPayable != null ? fmtINR(estimatedPayable) : "—"}
                  </span>
                </div>
                {pendingSavedAmount > 0 && (
                  <div style={{ fontSize: "11px", color: C.textSub, marginTop: "6px" }}>
                    Based on saved discounts not yet applied to the bill. Click "Apply Discount" to post this to the ledger.
                  </div>
                )}
              </div>
            );
          })()}
          {appliedDone && (
            <div style={{ fontSize: "12px", color: C.success, fontWeight: "600" }}>
              Discount applied. Total Payable {fmtINR(Number(preview?.net_payable ?? totalPayable))}.
            </div>
          )}
          {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error}</div>}
        </div>

        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <IpdButton variant="outlined" onClick={onClose} disabled={busy} sx={{ flex: 1 }}>Close</IpdButton>
          <IpdButton
            variant="outlined"
            onClick={handleReset}
            disabled={busy || !canReset}
            sx={{ flex: 1, color: C.danger, borderColor: C.danger, "&:hover": { borderColor: C.danger, backgroundColor: "#fdecea" } }}
          >Reset</IpdButton>
          <IpdButton
            variant="outlined"
            onClick={handleSave}
            disabled={busy || !hasStagedDiscount}
            title="Save the discount without posting it to the bill"
            sx={{ flex: 1.3 }}
          >Save</IpdButton>
          <IpdButton
            onClick={handleApply}
            disabled={busy || !canApply}
            loading={busy}
            title="Post saved discounts to the bill"
            sx={{ flex: 1.6 }}
          >Apply Discount</IpdButton>
        </div>
      </div>
    </div>
  );
}

// ── PAGE ──────────────────────────────────────────────────────────────────────
export default function IPDBillingInvoice() {
  const location      = useLocation();
  const passedPatient = location.state?.patient || null;
  const { can } = useBillingPermissions();
  const brandingData  = useContext(BrandingContext);
  // Facility address / statutory details for the printed letterhead.
  const { userInfo }  = usePermissions();

  const [activeAdmissionId, setActiveAdmissionId] = useState(() => resolveAdmissionId(passedPatient));
  const [selectedPatient,   setSelectedPatient]   = useState(() => {
    if (passedPatient?.admission_id || passedPatient?.admissionId) return passedPatient;
    return restoreAdmissionContext();
  });
  const [admissions,        setAdmissions]        = useState([]);
  const [searchQuery,       setSearchQuery]       = useState("");
  const [loadingAdmissions, setLoadingAdmissions] = useState(false);
  const [searchError,       setSearchError]       = useState("");

  const [invoice,           setInvoice]           = useState(null);
  const [split,             setSplit]             = useState([]);
  const [payments,          setPayments]          = useState([]);
  const [settlementSteps,   setSettlementSteps]   = useState([]);
  const [invoices,          setInvoices]          = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [isLoading,         setIsLoading]         = useState(false);
  const [busy,              setBusy]              = useState(false);
  const [apiError,          setApiError]          = useState("");
  const [apiMessage,        setApiMessage]        = useState("");
  const [showGenerate,      setShowGenerate]      = useState(false);
  const [showPayment,       setShowPayment]       = useState(false);
  const [invoicePan,        setInvoicePan]        = useState(null); // PAN on file, shown near total for ₹1L+ bills
  const [showApplyDeposit,  setShowApplyDeposit]  = useState(false);
  const [showAddDiscount,   setShowAddDiscount]   = useState(false);
  const [showInvoiceCancel, setShowInvoiceCancel] = useState(false);
  const [printMeta,         setPrintMeta]         = useState(null); // { label, count }
  const [showPrintReset,    setShowPrintReset]    = useState(false);
  const [showPrintOptions,  setShowPrintOptions]  = useState(false);
  const [pdfPreview,        setPdfPreview]        = useState(null); // { doc, filename, subtitle }
  const [additionalDiscount, setAdditionalDiscount] = useState(null); // { amount, label } — held client-side until API is wired
  const [walletAvailable,   setWalletAvailable]   = useState(0);

  const loadAdmissions = useCallback(async () => {
    setLoadingAdmissions(true);
    setSearchError("");
    try {
      const list = await listAllocatedAdmissions("Allocated");
      setAdmissions(list);
    } catch (err) {
      console.error("Admissions fetch error:", err);
      setSearchError(err.message || "Failed to load patients");
      setAdmissions([]);
    } finally {
      setLoadingAdmissions(false);
    }
  }, []);

  useEffect(() => {
    loadAdmissions();
  }, [loadAdmissions]);

  // Fill in an id-only restore (name/bed/doctor/payer) once the list lands.
  useEffect(() => {
    const hydrated = hydrateRestoredPatient(selectedPatient, admissions);
    if (hydrated !== selectedPatient) {
      persistAdmissionContext(hydrated);
      setSelectedPatient(hydrated);
    }
  }, [admissions, selectedPatient]);

  useEffect(() => {
    if (passedPatient) {
      const id = resolveAdmissionId(passedPatient);
      if (id) {
        persistAdmissionContext(passedPatient);
        setActiveAdmissionId(id);
        setSelectedPatient(passedPatient);
      }
    }
  }, [passedPatient]);

  const handleSelectAdmission = useCallback((row) => {
    const patient = admissionToPatientContext(row);
    persistAdmissionContext(patient);
    setSelectedPatient(patient);
    setActiveAdmissionId(String(row.admission_id));
    setSearchQuery("");
    setApiError("");
    setApiMessage("");
  }, []);

  const clearInvoiceView = useCallback(() => {
    setInvoice(null);
    setSplit([]);
    setPayments([]);
    setSettlementSteps([]);
    setAdditionalDiscount(null);
  }, []);

  const handleClearPatient = useCallback(() => {
    setSelectedPatient(null);
    setActiveAdmissionId("");
    clearAdmissionContext();
    setSearchQuery("");
    clearInvoiceView();
    setInvoices([]);
    setSelectedInvoiceId(null);
    setWalletAvailable(0);
    setApiError("");
    setApiMessage("Search and select a patient to begin.");
  }, [clearInvoiceView]);

  const loadInvoiceScreen = useCallback(async (invoiceId) => {
    if (!activeAdmissionId || !invoiceId) return;
    setAdditionalDiscount(null);
    setPrintMeta(null);
    const screen = await getInvoiceScreen(activeAdmissionId, invoiceId);
    const mapped = mapInvoiceScreenToView(screen, activeAdmissionId);
    setInvoice(mapped);
    setSplit(pickSplitBilling(screen));
    const paymentList = await listPayments(activeAdmissionId).catch(() => []);
    setPayments(paymentList);
    setSettlementSteps(buildSettlementSteps(mapped, paymentList, screen));
  }, [activeAdmissionId]);

  const refreshInvoices = useCallback(async () => {
    if (!activeAdmissionId) {
      setApiError("");
      clearInvoiceView();
      setInvoices([]);
      setSelectedInvoiceId(null);
      return;
    }
    setIsLoading(true);
    setApiError("");
    setApiMessage("");
    try {
      const [list, depositList] = await Promise.all([
        listInvoices(activeAdmissionId),
        listDeposits(activeAdmissionId).catch(() => []),
      ]);
      setWalletAvailable(mapDepositsToWallet(depositList).available_balance);
      setInvoices(list);
      if (list.length === 0) {
        clearInvoiceView();
        setSelectedInvoiceId(null);
        setApiMessage("No invoices yet. Post charges, then generate an interim or final bill.");
        return;
      }
      // Single-draft lifecycle: once anything is issued that settled invoice is the
      // one to show; before issue there is a single DRAFT. Prefer the issued/settled
      // invoice, then the open draft, then the most recent row.
      const statusOf = (i) => String(i.invoice_status || "").toUpperCase();
      const preferred =
        list.find((i) => ["ISSUED", "PARTIALLY_PAID", "PAID"].includes(statusOf(i))) ||
        list.find((i) => statusOf(i) === "DRAFT") ||
        list[list.length - 1];
      const invoiceId = preferred.id || preferred.invoice_id;
      setSelectedInvoiceId(invoiceId);
      await loadInvoiceScreen(invoiceId);
    } catch (err) {
      console.error("Invoice fetch error:", err);
      setApiError(err.message || "Failed to load invoices");
      clearInvoiceView();
      setInvoices([]);
      setSelectedInvoiceId(null);
    } finally {
      setIsLoading(false);
    }
  }, [activeAdmissionId, loadInvoiceScreen, clearInvoiceView]);

  useEffect(() => {
    refreshInvoices();
  }, [refreshInvoices]);

  const handleInvoiceSelect = async (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setIsLoading(true);
    setApiError("");
    try {
      await loadInvoiceScreen(invoiceId);
    } catch (err) {
      setApiError(err.message || "Failed to load invoice");
      clearInvoiceView();
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = async (form) => {
    if (!activeAdmissionId) return;
    setBusy(true);
    setApiError("");
    try {
      const created = await generateInvoice(activeAdmissionId, {
        invoice_type: form.invoice_type,
        // Single-draft lifecycle: Generate always refreshes the one open DRAFT and
        // never issues. Issuing is the separate final-settlement step (handleIssue).
        issue: false,
        due_date: form.due_date,
        remarks: form.remarks || undefined,
        // Always re-apply the current adhoc-discount configs to the ledger before generating,
        // so the bill reflects every saved/edited discount even if the user didn't re-click
        // "Apply" (the backend apply is idempotent — it reconciles the discount ledger rows to
        // the active configs, it does not accumulate). Fixes: later/edited discounts being
        // dropped from the regenerated bill.
        apply_adhoc_discounts: true,
      });
      const invoiceId =
        created.invoice_id ||
        created.id ||
        created.invoice?.id ||
        created.invoice?.invoice_id;
      setApiMessage(`Invoice ${created.invoice_number || ""} generated.`);
      setShowGenerate(false);
      await refreshInvoices();
      if (invoiceId) await handleInvoiceSelect(invoiceId);
    } catch (err) {
      setApiError(err.message || "Invoice generation failed");
    } finally {
      setBusy(false);
    }
  };

  const handleIssue = async () => {
    if (!activeAdmissionId || !selectedInvoiceId) return;
    setBusy(true);
    setApiError("");
    try {
      await issueInvoice(activeAdmissionId, selectedInvoiceId);
      setApiMessage("Invoice issued.");
      await refreshInvoices();
      await handleInvoiceSelect(selectedInvoiceId);
    } catch (err) {
      setApiError(err.message || "Issue failed");
    } finally {
      setBusy(false);
    }
  };

  // Show the PAN on file next to the total for high-value bills (₹1,00,000 and above).
  useEffect(() => {
    const billTotal = Number(invoice?.net_amount ?? invoice?.net_payable ?? 0);
    if (!activeAdmissionId || billTotal < PAN_THRESHOLD) { setInvoicePan(null); return; }
    let alive = true;
    getPatientPan(activeAdmissionId, invoice?.id || invoice?.invoice_id)
      .then((res) => { if (alive) setInvoicePan(res?.panNumber || null); })
      .catch(() => { if (alive) setInvoicePan(null); });
    return () => { alive = false; };
  }, [activeAdmissionId, invoice?.id, invoice?.invoice_id, invoice?.net_amount]);

  const handleCollectPayment = async (form) => {
    if (!activeAdmissionId || !selectedInvoiceId) return;
    setBusy(true);
    setApiError("");
    try {
      const activeCounter = getActiveCashCounterSession();
      await collectPayment(activeAdmissionId, {
        payment_mode: PAYMENT_MODE_TO_API[form.mode] || form.mode,
        amount: form.amount,
        payment_status: "SUCCESS",
        invoice_id: selectedInvoiceId,
        pan_number: form.pan || undefined,
        pan_not_available: form.panNotAvailable || undefined,
        transaction_reference: form.reference || undefined,
        remarks: form.remarks || undefined,
        ...(activeCounter && {
          cash_counter_session_id: activeCounter.sessionId,
          counter_code: activeCounter.counterCode,
        }),
      });
      setApiMessage("Payment recorded.");
      setShowPayment(false);
      await refreshInvoices();
      await handleInvoiceSelect(selectedInvoiceId);
    } catch (err) {
      setApiError(err.message || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  const handleApplyDeposit = async (form) => {
    if (!activeAdmissionId || !selectedInvoiceId) return;
    setBusy(true);
    setApiError("");
    try {
      await adjustDepositsRefunds(activeAdmissionId, {
        adjustment_type: "apply_to_bill",
        allocations: [{ invoice_id: selectedInvoiceId, amount: form.amount }],
        remarks: form.remarks || undefined,
      });
      setApiMessage("Deposit applied to invoice.");
      setShowApplyDeposit(false);
      await refreshInvoices();
      await handleInvoiceSelect(selectedInvoiceId);
    } catch (err) {
      setApiError(err.message || "Deposit allocation failed");
    } finally {
      setBusy(false);
    }
  };

  // ── Single-draft lifecycle state (drives every button) ──
  // An admission has at most ONE open DRAFT. Issuing any draft = final settlement
  // (ISSUED + FINAL): after that no new invoice can be generated, adhoc discounts
  // close, and deposit/payment turn on. Derive `issued` + `draftInvoiceId` from the
  // full invoice list so the buttons stay correct regardless of which row is loaded.
  const invStatus = (i) => String(i.invoice_status || "").toUpperCase();
  const issued = invoices.some((i) => ["ISSUED", "PARTIALLY_PAID", "PAID"].includes(invStatus(i)));
  const draftInvoice = invoices.find((i) => invStatus(i) === "DRAFT");
  const draftInvoiceId = draftInvoice ? (draftInvoice.id || draftInvoice.invoice_id) : null;
  // The single ISSUED/settling invoice (cancel + settlement target).
  const issuedInvoice = invoices.find((i) => ["ISSUED", "PARTIALLY_PAID", "PAID"].includes(invStatus(i)));
  const issuedInvoiceId = issuedInvoice ? (issuedInvoice.id || issuedInvoice.invoice_id) : null;

  const depositAvailable = Number(walletAvailable || 0);
  // Patient's real remaining due (co-pay + deposit aware): insurance case → patient_payable_amt
  // (net − paid − insurance), else net_payable (net − paid). Drives the collect box + button.
  const patientDue = Number(invoice?.insurance_approved) > 0
    ? Number(invoice?.patient_payable_amt || 0)
    : Number(invoice?.net_payable || 0);
  // Deposit adjust + collect payment turn on only after issue (backend blocks them before).
  // Hide once the patient owes nothing — insurance-approved + deposits + payments cover the bill.
  const canCollectPayment = issued && patientDue > 0.01 && ["ISSUED", "PARTIALLY_PAID"].includes(invStatus(invoice || {}));
  const canApplyDeposit = canCollectPayment && depositAvailable > 0;
  // Issue the draft only while nothing has been issued yet.
  const canIssue = Boolean(draftInvoiceId) && !issued;
  // Adhoc/additional discount is a draft-phase action — hidden once issued.
  const canAddDiscount = Boolean(activeAdmissionId) && !isLoading && !issued;
  const hasInvoice = Boolean(invoice?.invoice_id);

  // ── Invoice cancel (Part E) ──
  const isPaymentActive = (p) => {
    const status = String(p.payment_status || p.status || "").toUpperCase();
    return status !== "CANCELLED" && status !== "FAILED";
  };
  // A payment counts against *this* invoice if it has an active allocation to it
  // (or no allocation info at all — then assume it's relevant).
  const allocatedToInvoice = (p) => {
    const allocs = p.allocations || p.allocation || [];
    if (!Array.isArray(allocs) || allocs.length === 0) return true;
    return allocs.some((a) => {
      const sameInvoice = !selectedInvoiceId || String(a.invoice_id) === String(selectedInvoiceId);
      const activeAlloc = String(a.allocation_status || "").toUpperCase() !== "CANCELLED";
      return sameInvoice && activeAlloc;
    });
  };
  const invoiceStatus = String(invoice?.invoice_status || "").toUpperCase();
  // An ISSUED invoice is awaiting settlement — nothing has been paid against it, so
  // no receipt can be blocking its cancellation. Only look for blocking receipts once
  // money has actually landed on the invoice; otherwise an unallocated payment
  // elsewhere on the admission (a deposit, another invoice's receipt) would trip the
  // `allocatedToInvoice` fallback above and wrongly block the cancel.
  const isSettledInvoice =
    ["PARTIALLY_PAID", "PAID"].includes(invoiceStatus) ||
    Number(invoice?.paid_amount || 0) > 0;
  const activeReceipts = isSettledInvoice
    ? payments.filter((p) => isPaymentActive(p) && allocatedToInvoice(p))
    : [];
  const hasActivePayment = activeReceipts.length > 0;
  const isInvoiceCancelled = invoiceStatus === "CANCELLED";
  // Single-draft lifecycle: direct cancel is ISSUED-only — a DRAFT is never
  // cancelled (regenerate instead), so the button appears only once an invoice is
  // issued. If there's an active (SUCCESS) payment, clicking it shows a "cancel the
  // payment(s) first" error instead of the modal (backend rejects — refund first).
  const canRequestInvoiceCancel =
    issued &&
    !isInvoiceCancelled &&
    can(BILLING_PERMS.INVOICE_CANCEL_REQUEST);

  const handleInvoiceCancelClick = () => {
    if (hasActivePayment) {
      const nums = activeReceipts
        .map((r) => r.payment_number || r.receipt_number || r.number)
        .filter(Boolean)
        .join(", ");
      setApiMessage("");
      setApiError(
        `Cancel the payment${activeReceipts.length > 1 ? "s" : ""} first${nums ? ` (${nums})` : ""} before cancelling this invoice.`
      );
      return;
    }
    setApiError("");
    setShowInvoiceCancel(true);
  };

  const handleRequestInvoiceCancel = async (reason) => {
    const cancelId = issuedInvoiceId || selectedInvoiceId;
    if (!activeAdmissionId || !cancelId) return;
    setBusy(true);
    setApiError("");
    try {
      // Direct cancel of the ISSUED invoice — `remarks` is required by the API.
      // Cancelling releases its charges back to uninvoiced, so the admission resets
      // to the draft phase (day-0): Generate + adhoc reappear, deposit re-locks.
      await requestInvoiceCancel(activeAdmissionId, cancelId, { remarks: reason });
      setApiMessage("Invoice cancelled. Charges released — you can regenerate the bill.");
      setShowInvoiceCancel(false);
      await refreshInvoices();
    } catch (err) {
      setApiError(toBillingError(err));
    } finally {
      setBusy(false);
    }
  };

  // Refresh the page's invoice view after a discount change. (The modal refreshes
  // its own bill-preview + saved-config list separately.)
  const refreshAfterDiscountChange = async () => {
    await refreshInvoices();
    if (selectedInvoiceId) await handleInvoiceSelect(selectedInvoiceId);
  };

  // Create the staged config(s) WITHOUT touching the ledger (Save phase).
  //   TOTAL → single create; multi-target levels → batch (upsert per scope).
  const createDiscountConfigs = async (form) => {
    const approvedBy =
      sessionStorage.getItem("userName") || sessionStorage.getItem("username") || "billing.desk";
    const idField = {
      GROUP: "emr_service_group_id",
      CLASSIFICATION: "emr_service_classification_id",
      SERVICE: "emr_medical_service_id",
      PACKAGE: "package_assignment_id",
    }[form.discount_level];
    if (form.discount_level === "TOTAL") {
      const line = form.lines[0];
      await createAdhocDiscount(activeAdmissionId, {
        discount_level: "TOTAL",
        discount_type: line.discount_type,
        discount_value: Number(line.discount_value).toFixed(2),
        reason: form.reason,
        ...(form.remarks ? { remarks: form.remarks } : {}),
        approved_by: approvedBy,
      });
    } else {
      await createAdhocDiscountsBatch(activeAdmissionId, {
        reason: form.reason,
        ...(form.remarks ? { remarks: form.remarks } : {}),
        approved_by: approvedBy,
        apply_after_create: false,
        items: form.lines.map((line) => ({
          discount_level: form.discount_level,
          [idField]: line.id,
          discount_type: line.discount_type,
          discount_value: Number(line.discount_value).toFixed(2),
          ...(line.name ? { target_name: line.name } : {}),
        })),
      });
    }
  };

  // Save phase — persist config(s); ledger/bill totals stay unchanged until Apply.
  const handleSaveDiscount = async (form) => {
    if (!activeAdmissionId) return;
    setBusy(true); setApiError("");
    try {
      await createDiscountConfigs(form);
      setApiMessage(`Additional discount saved${form.target_name ? ` for "${form.target_name}"` : ""}. Click Apply to post it to the bill.`);
    } catch (err) {
      setApiError(err.message || "Failed to save additional discount");
      throw err;
    } finally { setBusy(false); }
  };

  // Apply phase — save any staged config first, then commit ALL active configs to
  // the ledger (both line + bill discounts).
  const handleApplyDiscount = async (form) => {
    if (!activeAdmissionId) return;
    setBusy(true); setApiError("");
    try {
      if (form?.lines?.length) await createDiscountConfigs(form);
      await applyAdhocDiscounts(activeAdmissionId, {
        dry_run: false,
        apply_line_discounts: true,
        apply_bill_discount: true,
      });
      setApiMessage("Additional discounts applied to the bill.");
      await refreshAfterDiscountChange();
    } catch (err) {
      setApiError(err.message || "Failed to apply additional discount");
      throw err; // let the modal know the apply failed
    } finally { setBusy(false); }
  };

  // Edit one discount. `reapplyLedger` re-syncs the ledger only when it's already applied.
  const handleUpdateDiscount = async (discountId, patch, reapplyLedger = true) => {
    if (!activeAdmissionId) return;
    setBusy(true); setApiError("");
    try {
      await updateAdhocDiscount(activeAdmissionId, discountId, { ...patch, reapply_ledger: reapplyLedger });
      if (reapplyLedger) await refreshAfterDiscountChange();
      setApiMessage("Additional discount updated.");
    } catch (err) {
      setApiError(err.message || "Failed to update discount");
      throw err;
    } finally { setBusy(false); }
  };

  // Remove one discount via revoke (deactivates config; re-syncs ledger when applied).
  const handleRemoveDiscount = async (discountId, reapplyLedger = true) => {
    if (!activeAdmissionId) return;
    setBusy(true); setApiError("");
    try {
      await revokeAdhocDiscount(activeAdmissionId, discountId, { reapplyLedger });
      if (reapplyLedger) await refreshAfterDiscountChange();
      setApiMessage("Additional discount removed.");
    } catch (err) {
      setApiError(err.message || "Failed to remove discount");
      throw err;
    } finally { setBusy(false); }
  };

  // Clear all configs + adhoc ledger rows in one call.
  const handleResetDiscounts = async () => {
    if (!activeAdmissionId) return;
    setBusy(true); setApiError("");
    try {
      await resetAdhocDiscounts(activeAdmissionId, { reapplyLedger: true });
      await refreshAfterDiscountChange();
      setApiMessage("All additional discounts cleared.");
    } catch (err) {
      setApiError(err.message || "Failed to clear discounts");
      throw err;
    } finally { setBusy(false); }
  };

  // `options` = { letterhead, format } from PrintOptionsModal — both drive how the
  // PDF is built (see generateInvoicePdf).
  // `options` = { letterhead, format } from PrintOptionsModal — both drive how the
  // PDF is built (see generateInvoicePdf).
  const handlePrint = async (options) => {
    if (!hasInvoice || !invoice) return;
    setShowPrintOptions(false);
    // Record the print so the backend can tag it ORIGINAL / DUPLICATE. If the
    // print endpoint isn't available, fall back to an unlabelled print.
    let label = "ORIGINAL";
    try {
      const res = await printDocument(activeAdmissionId, {
        document_type: "INVOICE",
        document_id: invoice.invoice_id,
        document_number: invoice.invoice_no || invoice.invoice_number,
      });
      label = String(res.print_label || res.label || "ORIGINAL").toUpperCase();
      setPrintMeta({
        label,
        count: res.print_counter ?? res.print_sequence ?? res.print_count ?? res.count ?? null,
      });
    } catch (err) {
      setPrintMeta((prev) => prev || { label: "ORIGINAL", count: null });
    }
    // Standardized jsPDF + autoTable bill (Hlthtek header/footer band).
    const fmt = String(options?.format || "COMBINED").toUpperCase();
    const doc = generateInvoicePdf({
      invoice,
      brandingData,
      userInfo,
      label,
      format: fmt,
      letterhead: options?.letterhead !== "WITHOUT",
    });
    // Open the tab only once the PDF exists, so no blank tab sits there while it builds.
    // The awaits above can outlive the click's user-activation, in which case the browser
    // blocks the popup and window.open returns null — the in-page preview then takes over
    // (the reason PdfPreviewModal exists).
    if (window.open(`${doc.output("bloburl")}#zoom=page-fit`, "_blank")) return;
    setPdfPreview({
      doc,
      // Format in the filename so a downloaded bill says which one it is.
      filename: `Bill-${invoice.invoice_no || invoice.invoice_id || activeAdmissionId}-${fmt}.pdf`,
      subtitle: `${invoice.invoice_no || ""} · ${FORMAT_LABEL[fmt] || fmt} · ${label}`,
    });
  };

  const handleRequestPrintReset = async (reason) => {
    if (!activeAdmissionId || !invoice?.invoice_id) return;
    setBusy(true);
    setApiError("");
    try {
      await requestPrintReset(activeAdmissionId, "INVOICE", invoice.invoice_id, { reason });
      setApiMessage("Original-reprint request submitted for approval.");
      setShowPrintReset(false);
    } catch (err) {
      setApiError(toBillingError(err));
    } finally {
      setBusy(false);
    }
  };

  const maxSplitAmount = Math.max(...split.map((s) => s.amount), 1);

  const addlDiscountAmount = Number(additionalDiscount?.amount || 0);
  const totalPayableAfter = Math.max(0, Number(invoice?.total_payable || 0) - addlDiscountAmount);
  // net_payable is derived in the mapper from the invoice net (net_amount − deposits −
  // paid), so on a package invoice it reads the full 51,100 rather than the backend's
  // outstanding/patient_payable figure (1,100) that excludes the package price.
  const netPayableAfter = Math.max(0, Number(invoice?.net_payable || 0) - addlDiscountAmount);
  // Insurance co-pay: when the insurer has an approved amount, the patient only owes the
  // balance — surface that as "Patient Payable" on the invoice screen.
  const insuranceApproved = Number(invoice?.insurance_approved || 0);
  const showCopay = insuranceApproved > 0;
  // patient_payable_amt comes from the billing account outstanding (net − insurance −
  // deposits − paid, each counted once), so display it directly — don't re-subtract
  // deposits/paid here or the adjusted deposit gets double-counted.
  const patientPayableAfter = Math.max(0, Number(invoice?.patient_payable_amt || 0) - addlDiscountAmount);

  const generateButton = (
    <IpdButton
      onClick={() => setShowGenerate(true)}
      disabled={busy || !activeAdmissionId || issued}
      startIcon={<SvgIcon name="plus" size={15} />}
    >
      Generate Bill
    </IpdButton>
  );

  return (
    <IPDBillingLayout activeNav="invoice">
      <div style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "20px" }}>

        {/* ── PATIENT SEARCH ── */}
        <PatientSearchBar
          admissions={admissions}
          loadingAdmissions={loadingAdmissions}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSelect={handleSelectAdmission}
          selectedPatient={selectedPatient}
          onClear={handleClearPatient}
          onRefresh={loadAdmissions}
          searchError={searchError}
          selectHint="select one to load invoices"
        />

        {/* ── PAGE TITLE + ACTIONS ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: "22px", fontWeight: "800", color: C.text }}>Invoice & Settlement</h2>
            <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>
              {invoice?.invoice_type ? `${invoice.invoice_type} bill` : "Invoice & settlement"} · Admission {activeAdmissionId || "—"}
            </p>
            {/* Single-draft lifecycle: an admission has at most one open draft (plus,
                once issued, the single settled invoice), so the multi-draft picker is
                gone — refreshInvoices() auto-selects the issued invoice, else the draft. */}
            {apiError && <p style={{ margin: "6px 0 0", fontSize: "12px", color: C.danger, fontWeight: "600" }}>{apiError}</p>}
            {apiMessage && !apiError && <p style={{ margin: "6px 0 0", fontSize: "12px", color: C.success, fontWeight: "600" }}>{apiMessage}</p>}
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {activeAdmissionId && (
              <>
                <IpdButton variant="outlined" onClick={() => setShowGenerate(true)}
                  disabled={busy || issued}
                  title={issued ? "An invoice has been issued — no further invoices can be generated" : undefined}
                  startIcon={<SvgIcon name="plus" size={15} />}>
                  Generate Bill
                </IpdButton>
                {canAddDiscount && (
                  <IpdButton variant="outlined" onClick={() => setShowAddDiscount(true)} disabled={busy}
                    startIcon={<SvgIcon name="percent" size={15} />}>
                    Additional Discount
                  </IpdButton>
                )}
                {canIssue && (
                  <IpdButton variant="outlined" onClick={handleIssue} disabled={busy}
                    startIcon={<SvgIcon name="check" size={15} />}>
                    Issue Invoice
                  </IpdButton>
                )}
                {canApplyDeposit && (
                  <IpdButton onClick={() => setShowApplyDeposit(true)} disabled={busy}
                    startIcon={<SvgIcon name="wallet" size={15} />}>
                    Apply Deposit ({fmtINR(depositAvailable)})
                  </IpdButton>
                )}
                {canCollectPayment && (
                  <IpdButton tone="mint" onClick={() => setShowPayment(true)} disabled={busy}
                    startIcon={<SvgIcon name="wallet" size={15} />}>
                    Collect Payment
                  </IpdButton>
                )}
              </>
            )}
            <IpdButton
              variant="outlined"
              onClick={() => setShowPrintOptions(true)}
              disabled={!hasInvoice}
              startIcon={<SvgIcon name="printer" size={15} />}
            >
              {printMeta?.label === "DUPLICATE" ? "Reprint (Duplicate)" : "Print"}
              {printMeta?.count ? (
                <span style={{ marginLeft: "6px", fontSize: "10px", fontWeight: "700", background: "#eef1f4", color: C.textSub, borderRadius: "100px", padding: "1px 7px" }}>×{printMeta.count}</span>
              ) : null}
            </IpdButton>
            {printMeta && (printMeta.label === "DUPLICATE" || (printMeta.count || 0) > 1) && can(BILLING_PERMS.PRINT_COUNTER_RESET_REQUEST) && (
              <IpdButton
                variant="outlined"
                onClick={() => setShowPrintReset(true)}
                disabled={busy}
                title="Reset counter so the next print is ORIGINAL"
                startIcon={<SvgIcon name="refresh" size={15} />}
              >
                Request Original Reprint
              </IpdButton>
            )}
            {canRequestInvoiceCancel && (
              <IpdButton
                variant="outlined"
                onClick={handleInvoiceCancelClick}
                disabled={busy}
                title={hasActivePayment ? "Cancel the payment(s) first" : "Cancel this issued invoice"}
                startIcon={<SvgIcon name="x" size={15} />}
              >
                Cancel Invoice
              </IpdButton>
            )}
          </div>
        </div>

        {/* ── TWO-COLUMN LAYOUT ── */}
        <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>

          {/* ── LEFT: INVOICE CARD ── */}
          <div style={{ flex: 1, minWidth: 0, background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
            {isLoading ? (
              <div style={{ padding: "32px", display: "flex", flexDirection: "column", gap: "16px" }}>
                {[80, 60, 40, 70, 50, 90, 55, 65].map((w, i) => (
                  <div key={i} style={{ height: "14px", width: `${w}%`, ...shimmerStyle }} />
                ))}
              </div>
            ) : !invoice ? (
              <EmptyState
                title={!activeAdmissionId ? "No patient selected" : "No invoice to display"}
                message={
                  !activeAdmissionId
                    ? "Search and select an allocated patient above to load their invoices."
                    : apiError || "Post charges on the ledger, then generate an interim or final invoice."
                }
                action={activeAdmissionId ? generateButton : null}
              />
            ) : (
              <div style={{ padding: "28px 32px", position: "relative" }}>

                {/* ── INVOICE HEADER ── */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
                    <div style={{ width: "48px", height: "48px", borderRadius: "10px", background: invoice.hospital.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", fontWeight: "800", color: "#fff", flexShrink: 0 }}>
                      {invoice.hospital.initial}
                    </div>
                    <div>
                      <div style={{ fontSize: "16px", fontWeight: "800", color: C.text, marginBottom: "3px" }}>{invoice.hospital.name}</div>
                      {invoice.hospital.address && (
                        <div style={{ fontSize: "12px", color: C.textSub, marginBottom: "1px" }}>{invoice.hospital.address}</div>
                      )}
                      {invoice.hospital.gstin && (
                        <div style={{ fontSize: "12px", color: C.textSub }}>GSTIN {invoice.hospital.gstin}</div>
                      )}
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "10px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>TAX INVOICE</div>
                    <div style={{ fontSize: "22px", fontWeight: "800", color: C.text, marginBottom: "4px", letterSpacing: "-0.01em" }}>{invoice.invoice_no}</div>
                    <div style={{ fontSize: "12px", color: C.textSub, marginBottom: "8px" }}>
                      Issued {invoice.issued_date} · Admission {invoice.encounter_id}
                    </div>
                    <span style={{ fontSize: "12px", fontWeight: "700", padding: "4px 12px", borderRadius: "100px", ...(isInvoiceCancelled ? { background: "#fdecea", color: C.danger, border: "1px solid #f5c2c7" } : { background: "#fff8e1", color: "#856404", border: "1px solid #ffe69c" }) }}>
                      {invoice.status}
                    </span>
                  </div>
                </div>

                {/* ── PATIENT / DOCTOR / PAYER ── */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "24px", paddingBottom: "24px", borderBottom: `1.5px solid ${C.border}`, marginBottom: "24px" }}>
                  <div>
                    <div style={{ fontSize: "10px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "6px" }}>Patient</div>
                    <div style={{ fontSize: "15px", fontWeight: "700", color: C.text, marginBottom: "3px" }}>
                      {invoice.patient.name} · {invoice.patient.age} / {invoice.patient.gender}
                    </div>
                    <div style={{ fontSize: "12px", color: C.textSub, display: "flex", alignItems: "center", gap: "6px" }}>
                      UHID: {invoice.patient.uhid}
                      {invoice.patient.abha && (
                        <span style={{ fontSize: "10px", fontWeight: "700", padding: "1px 6px", borderRadius: "100px", background: C.accent, color: C.primary, border: "1px solid #b8d4ee" }}>ABHA verified</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "10px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "6px" }}>Treating Doctor</div>
                    <div style={{ fontSize: "15px", fontWeight: "700", color: C.text, marginBottom: "3px" }}>{invoice.doctor.name}</div>
                    <div style={{ fontSize: "12px", color: C.textSub }}>{invoice.doctor.specialty} · {invoice.doctor.ward}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "10px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "6px" }}>Payer</div>
                    <div style={{ fontSize: "15px", fontWeight: "700", color: C.text, marginBottom: "3px" }}>
                      {invoice.payer.name} · {invoice.payer.type}
                    </div>
                    {(invoice.payer.policy !== "—" || invoice.payer.preauth > 0) && (
                      <div style={{ fontSize: "12px", color: C.textSub }}>
                        {invoice.payer.policy !== "—" && `Policy # ${invoice.payer.policy}`}
                        {invoice.payer.preauth > 0 && ` · Pre-auth ${fmtINR(invoice.payer.preauth)}`}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── LINE ITEMS TABLE ── */}
                {(() => {
                  const invoiceColumns = [
                    { field: 'description', headerName: 'Description', flex: 1, renderCell: (p) => p.value },
                    { field: 'dept', headerName: 'Dept', width: 140 },
                    { field: 'qty', headerName: 'Qty', width: 80, headerAlign: 'right', align: 'right' },
                    { field: 'rate', headerName: 'Rate', width: 120, headerAlign: 'right', align: 'right', valueFormatter: (p) => fmtINR(p.value) },
                    { field: 'amount', headerName: 'Amount', width: 140, headerAlign: 'right', align: 'right', valueFormatter: (p) => fmtINR(p.value) },
                  ];
                  return (
                    <div style={{ marginBottom: '28px' }}>
                      <IpdDataGrid rows={invoice.line_items || []} columns={invoiceColumns} getRowId={(r) => r.id} pageSize={100} hideFooter={(invoice.line_items || []).length <= 100} autoHeight />
                    </div>
                  );
                })()}
                

                {/* ── TOTALS SECTION ── */}
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ width: "340px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
                      <span style={{ fontSize: "14px", color: C.textSub }}>Subtotal</span>
                      <span style={{ fontSize: "14px", color: C.text }}>{fmtINR(invoice.subtotal)}</span>
                    </div>
                    {invoice.discount > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
                        <span style={{ fontSize: "14px", color: C.textSub }}>Discount ({invoice.discount_label})</span>
                        <span style={{ fontSize: "14px", color: C.danger, fontWeight: "600" }}>– {fmtINR(invoice.discount)}</span>
                      </div>
                    )}
                    {addlDiscountAmount > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
                        <span style={{ fontSize: "14px", color: C.textSub }}>Additional Discount ({additionalDiscount.label})</span>
                        <span style={{ fontSize: "14px", color: C.danger, fontWeight: "600" }}>– {fmtINR(addlDiscountAmount)}</span>
                      </div>
                    )}
                    {invoice.gst_amount > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
                        <span style={{ fontSize: "14px", color: C.textSub }}>GST {invoice.gst_rate > 0 ? `${invoice.gst_rate}%` : ""}</span>
                        <span style={{ fontSize: "14px", color: C.text }}>{fmtINR(invoice.gst_amount)}</span>
                      </div>
                    )}

                    <div style={{ borderTop: `1.5px solid ${C.border}`, margin: "10px 0" }} />

                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                      <span style={{ fontSize: "15px", fontWeight: "700", color: C.text }}>Total Payable</span>
                      <span style={{ fontSize: "15px", fontWeight: "700", color: C.text }}>{fmtINR(totalPayableAfter)}</span>
                    </div>
                    {invoice.deposits_adjusted > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
                        <span style={{ fontSize: "14px", color: C.textSub }}>Deposits adjusted</span>
                        <span style={{ fontSize: "14px", color: C.success, fontWeight: "600" }}>– {fmtINR(invoice.deposits_adjusted)}</span>
                      </div>
                    )}
                    {showCopay && (
                      <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
                        <span style={{ fontSize: "14px", color: C.textSub }}>
                          Payable by {invoice.insurance_payer_name || invoice.payer?.name || "Insurer"} (approved)
                        </span>
                        <span style={{ fontSize: "14px", color: C.success, fontWeight: "600" }}>– {fmtINR(insuranceApproved)}</span>
                      </div>
                    )}

                    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 12px", background: C.accent, borderRadius: "8px", border: `1px solid #b8d4ee`, marginTop: "6px" }}>
                      <span style={{ fontSize: "16px", fontWeight: "800", color: C.text }}>{showCopay ? "Patient Payable" : "Net Payable"}</span>
                      <span style={{ fontSize: "20px", fontWeight: "800", color: C.primary }}>{fmtINR(showCopay ? patientPayableAfter : netPayableAfter)}</span>
                    </div>
                    {invoicePan && (
                      <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 12px", marginTop: "4px" }}>
                        <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub }}>PAN</span>
                        <span style={{ fontSize: "13px", fontWeight: "700", color: C.text, letterSpacing: "0.5px" }}>{invoicePan}</span>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            )}
          </div>

          {/* ── RIGHT PANEL (screen only) ── */}
          <div className="ipd-invoice-no-print" style={{ width: "260px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "16px" }}>

            {/* Split Billing */}
            <div style={{ background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)", padding: "18px 20px" }}>
              <h3 style={{ margin: "0 0 18px", fontSize: "14px", fontWeight: "700", color: C.text }}>Split Billing</h3>
              {isLoading ? (
                [1, 2, 3].map((i) => (
                  <div key={i} style={{ marginBottom: "16px", display: "flex", flexDirection: "column", gap: "6px" }}>
                    <div style={{ height: "12px", width: "80%", ...shimmerStyle }} />
                    <div style={{ height: "5px", width: "100%", ...shimmerStyle }} />
                  </div>
                ))
              ) : split.length === 0 ? (
                <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>No split billing data from API.</p>
              ) : (
                split.map((item) => {
                  const pct = (item.amount / maxSplitAmount) * 100;
                  return (
                    <div key={item.payer} style={{ marginBottom: "14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                        <span style={{ fontSize: "13px", color: C.textSub }}>{item.payer}</span>
                        <span style={{ fontSize: "13px", fontWeight: "700", color: C.text }}>{fmtINR(item.amount)}</span>
                      </div>
                      <div style={{ height: "6px", background: "#e9ecef", borderRadius: "100px", overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: pct > 50 ? C.primary : "#adb5bd", borderRadius: "100px", transition: "width 0.6s ease" }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Deposit wallet */}
            {depositAvailable > 0 && (
              <button
                type="button"
                onClick={() => canApplyDeposit && setShowApplyDeposit(true)}
                disabled={!canApplyDeposit || busy}
                title={canApplyDeposit ? "Apply deposit to this invoice" : "Issue the invoice first to apply deposit"}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: C.accent,
                  borderRadius: "12px",
                  border: `1px solid ${canApplyDeposit ? C.primary : "#b8d4ee"}`,
                  padding: "14px 16px",
                  cursor: canApplyDeposit && !busy ? "pointer" : "default",
                  opacity: busy ? 0.7 : 1,
                  transition: "box-shadow 0.15s, border-color 0.15s",
                  boxShadow: canApplyDeposit ? "0 2px 8px rgba(13,35,63,0.08)" : "none",
                }}
                onMouseEnter={(e) => {
                  if (canApplyDeposit && !busy) {
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(13,35,63,0.14)";
                    e.currentTarget.style.borderColor = C.primary;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = canApplyDeposit ? "0 2px 8px rgba(13,35,63,0.08)" : "none";
                  e.currentTarget.style.borderColor = canApplyDeposit ? C.primary : "#b8d4ee";
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                  <div style={{ fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>Deposit wallet</div>
                  {canApplyDeposit && <SvgIcon name="wallet" size={14} color={C.primary} />}
                </div>
                <div style={{ fontSize: "18px", fontWeight: "800", color: C.primary }}>{fmtINR(depositAvailable)}</div>
                <div style={{ fontSize: "11px", color: canApplyDeposit ? C.primary : C.textSub, marginTop: "4px", fontWeight: canApplyDeposit ? "600" : "400" }}>
                  {canApplyDeposit ? "Click to apply against this bill →" : "Available — issue invoice to apply"}
                </div>
              </button>
            )}

            {/* Settlement Status */}
            <div style={{ background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)", padding: "18px 20px" }}>
              <h3 style={{ margin: "0 0 18px", fontSize: "14px", fontWeight: "700", color: C.text }}>Settlement Status</h3>
              {isLoading ? (
                [1, 2, 3].map((i) => (
                  <div key={i} style={{ marginBottom: "18px", display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{ width: "12px", height: "12px", borderRadius: "50%", ...shimmerStyle, flexShrink: 0 }} />
                    <div style={{ height: "12px", width: "70%", ...shimmerStyle }} />
                  </div>
                ))
              ) : settlementSteps.length === 0 ? (
                <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>Settlement steps appear after an invoice is loaded.</p>
              ) : (
                settlementSteps.map((step, idx) => {
                  const isDone   = step.status === "done";
                  const isActive = step.status === "in-progress";
                  const dotColor = isDone ? C.success : isActive ? "#0ea5e9" : C.border;
                  const textColor = isDone || isActive ? C.text : C.textSub;
                  const last = idx === settlementSteps.length - 1;
                  return (
                    <div key={step.label} style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                        <div style={{ width: "12px", height: "12px", borderRadius: "50%", background: isDone || isActive ? dotColor : "#fff", border: `2px solid ${dotColor}`, marginTop: "2px" }} />
                        {!last && <div style={{ width: "2px", height: "22px", background: isDone ? C.success : "#e9ecef", marginTop: "2px" }} />}
                      </div>
                      <div style={{ paddingBottom: last ? 0 : "10px" }}>
                        <span style={{ fontSize: "13px", fontWeight: isDone || isActive ? "600" : "400", color: textColor }}>
                          {step.label}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

          </div>
        </div>
      </div>

      {showInvoiceCancel && invoice && (
        <RequestInvoiceCancelModal
          invoice={invoice}
          activeReceipts={activeReceipts}
          onClose={() => setShowInvoiceCancel(false)}
          onSubmit={handleRequestInvoiceCancel}
          busy={busy}
        />
      )}
      {showPrintReset && (
        <PrintResetModal onClose={() => setShowPrintReset(false)} onSubmit={handleRequestPrintReset} busy={busy} />
      )}
      {showPrintOptions && hasInvoice && (
        <PrintOptionsModal onClose={() => setShowPrintOptions(false)} onSubmit={handlePrint} busy={busy} />
      )}
      {pdfPreview && (
        <PdfPreviewModal
          doc={pdfPreview.doc}
          filename={pdfPreview.filename}
          title="Bill Preview"
          subtitle={pdfPreview.subtitle}
          onClose={() => setPdfPreview(null)}
        />
      )}
      {showGenerate && (
        <GenerateInvoiceModal onClose={() => setShowGenerate(false)} onGenerate={handleGenerate} busy={busy} />
      )}
      {showPayment && invoice && (
        <CollectPaymentModal invoice={invoice} admissionId={activeAdmissionId} amountDue={showCopay ? patientPayableAfter : netPayableAfter} onClose={() => setShowPayment(false)} onSubmit={handleCollectPayment} busy={busy} />
      )}
      {showAddDiscount && activeAdmissionId && (
        <AdditionalDiscountModal
          invoice={invoice}
          admissionId={activeAdmissionId}
          applied={additionalDiscount}
          onClose={() => setShowAddDiscount(false)}
          onSaveDiscount={handleSaveDiscount}
          onApplyDiscount={handleApplyDiscount}
          onUpdateDiscount={handleUpdateDiscount}
          onRemoveDiscount={handleRemoveDiscount}
          onResetDiscounts={handleResetDiscounts}
          busy={busy}
        />
      )}
      {showApplyDeposit && invoice && depositAvailable > 0 && (
        <ApplyDepositModal
          invoice={invoice}
          avail={depositAvailable}
          onClose={() => setShowApplyDeposit(false)}
          onSubmit={handleApplyDeposit}
          busy={busy}
        />
      )}
    </IPDBillingLayout>
  );
}
